import React, { useState, useEffect, useRef } from 'react';
import { CONFIG, API_BASE as API } from './constants'
import NavBar from './components/NavBar';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import BillingPage from './components/BillingPage';
import Logbook from './components/Logbook';
import LoadingSpinner from './components/LoadingSpinner';
import PaymentModal from './components/PaymentModal';
import SwishQRModal from './components/SwishQRModal';
// Ensure CSS is properly imported
import './App.css';
// Import missing formatKr function
import { formatKr } from './utils/format';

import { mockFamilies, mockBookings, saveMockBookings, mockUpdateBooking, mockDeleteBooking, mockDeleteBookingsRange, mockLogin } from './mockApi';

// For production deployments on Cloudflare Pages with backend databases, use real data
// Removed mock mode - now connects to actual D1/R2 databases
const useMockData = window.location.hostname === 'localhost' || process.env.NODE_ENV === 'development';
const isLocalDevelopment = window.location.hostname === 'localhost';

// --- Swedish holidays helper ---
const easterSunday = (year) => {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isSameYMD = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatDate = (date) => {
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const midsummerDay = (year) => {
  // Saturday between June 20-26
  for (let d = 20; d <= 26; d++) {
    const dt = new Date(year, 5, d);
    if (dt.getDay() === 6) return dt;
  }
  return new Date(year, 5, 25);
};

const allSaintsDay = (year) => {
  // Saturday between Oct 31 and Nov 6
  for (let m = 9, d = 31; d <= 31; d++) {
    const start = new Date(year, m, d);
    for (let off = 0; off <= 7; off++) {
      const dt = addDays(start, off);
      if (dt.getMonth() === 10 && dt.getDate() > 6) break; // beyond Nov 6
      if (dt.getDay() === 6) return dt;
    }
  }
  return new Date(year, 10, 1);
};

const isSwedishHoliday = (date) => {
  const y = date.getFullYear();
  const fixed = [
    new Date(y, 0, 1),   // Nyårsdagen
    new Date(y, 0, 6),   // Trettondedag jul
    new Date(y, 4, 1),   // Första maj
    new Date(y, 5, 6),   // Nationaldagen
    new Date(y, 11, 25), // Juldagen
    new Date(y, 11, 26), // Annandag jul
  ];
  if (fixed.some((d) => isSameYMD(d, date))) return true;

  const easter = easterSunday(y);
  const goodFriday = addDays(easter, -2);
  const easterMonday = addDays(easter, 1);
  const ascension = addDays(easter, 39);
  const midsummer = midsummerDay(y);
  const allSaints = allSaintsDay(y);

  const movable = [goodFriday, easterMonday, ascension, midsummer, allSaints];
  return movable.some((d) => isSameYMD(d, date));
};

function App() {
  const [step, setStep] = useState(0);
  const [code, setCode] = useState("");
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState('');
  const [clickCount, setClickCount] = useState(0);
  // Håller endast [start, end] för spannet
  const [selectedDates, setSelectedDates] = useState([]);
  const [paidBookings, setPaidBookings] = useState(new Set()); // Track paid bookings
  const [electricityUsage, setElectricityUsage] = useState({}); // Track kWh usage per booking
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  // If Cloudflare Access or another proxy blocks JSON APIs, enable a local fallback
  const [useFallbackMock, setUseFallbackMock] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Track if initial data has been loaded

  const getBookingForDate = (date) => {
    // Tidszon-säker metod för att hämta bokning för ett specifikt datum
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const dateString = `${yyyy}-${mm}-${dd}`;
    return bookings.find(booking => booking.date === dateString);
  };

  // Year-bound revenue (same-year only), shown as text on billing page
  const getYearlyRevenue = (year = currentYear) => {
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year, 11, 31);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const effectiveEnd = endOfYear < today ? endOfYear : today;

    // Helper: count nights within [startOfYear, effectiveEnd]
    const calculateNightsInYear = (bookingSpans) => {
      return bookingSpans.reduce((total, span) => {
        const spanStart = span.start instanceof Date ? new Date(span.start) : new Date(span.start + 'T00:00:00');
        const spanEnd = span.end instanceof Date ? new Date(span.end) : new Date(span.end + 'T00:00:00');

        // Clamp to year window
        const from = spanStart < startOfYear ? startOfYear : spanStart;
        const toRaw = spanEnd < effectiveEnd ? spanEnd : effectiveEnd;
        const to = toRaw;

        if (to <= startOfYear) return total; // span ends before year window
        if (from > effectiveEnd) return total; // span starts after year window

        const nights = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
        return total + Math.max(nights, 0);
      }, 0);
    };

    // Nights + electricity for checkouts in year
    const familiesSet = new Set(families);
    let revenue = 0;
    familiesSet.forEach((familyName) => {
      const familyBookings = bookings.filter(b => b.family === familyName);
      const bookingSpans = groupBookings(familyBookings);
      const nights = calculateNightsInYear(bookingSpans);
      const nightsCost = nights * COST_PER_NIGHT;

      // Electricity only for checkout dates within the year and not in the future
      const electricityCostRaw = familyBookings.reduce((sum, booking) => {
        const bookingDate = new Date(booking.date + 'T00:00:00');
        bookingDate.setHours(0, 0, 0, 0);
        if (bookingDate.getFullYear() !== year) return sum;
        if (bookingDate > today) return sum;
        for (const span of bookingSpans) {
          const spanEndDate = span.end instanceof Date ? new Date(span.end) : new Date(span.end + 'T00:00:00');
          spanEndDate.setHours(0, 0, 0, 0);
          if (bookingDate.getTime() === spanEndDate.getTime()) {
            const kwh = parseFloat(electricityUsage[booking.id]) || 0;
            return sum + (kwh * COST_PER_KWH);
          }
        }
        return sum;
      }, 0);
      const electricityCost = Math.round(electricityCostRaw);

      revenue += nightsCost + electricityCost;
    });

    return revenue;
  };

  // Find the latest past checkout booking for a family and get/set kWh
  const getLatestCheckoutBookingForFamily = (familyName) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const familyBookings = bookings.filter(b => b.family === familyName);
    const familySpans = groupBookings(familyBookings);
    // Collect past span end dates with their booking entries
    const candidates = [];
    familySpans.forEach(span => {
      const endYMD = typeof span.end === 'string' ? span.end : formatDate(span.end);
      const endDate = new Date(endYMD + 'T00:00:00');
      endDate.setHours(0, 0, 0, 0);
      // Include today as a valid latest checkout
      if (endDate <= today) {
        const checkoutBooking = familyBookings.find(b => b.date === endYMD);
        if (checkoutBooking) {
          candidates.push({ date: endDate, booking: checkoutBooking });
        }
      }
    });
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => b.date - a.date); // latest first
    return candidates[0].booking;
  };

  const setElectricityUsageForLatestCheckout = (familyName, kwh) => {
    const checkoutBooking = getLatestCheckoutBookingForFamily(familyName);
    if (!checkoutBooking) return false;
    setElectricityUsage(prev => {
      const next = { ...prev, [checkoutBooking.id]: kwh };
      localStorage.setItem('electricityUsage', JSON.stringify(next));
      return next;
    });
    return true;
  };

  // Helper: get booking span for a booking ID
  const getBookingSpan = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return null;

    const familyBookings = bookings.filter(b => b.family === booking.family).sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Find which consecutive span contains this booking
    const targetDate = new Date(booking.date + 'T00:00:00');
    let spans = [];
    let currentSpan = null;

    // Group bookings into consecutive spans
    for (let i = 0; i < familyBookings.length; i++) {
      const bookingDate = new Date(familyBookings[i].date + 'T00:00:00');

      if (currentSpan === null) {
        // Start new span
        currentSpan = {
          family: booking.family,
          start: bookingDate,
          end: bookingDate,
          bookings: [familyBookings[i]]
        };
      } else {
        // Check if this booking is consecutive (next day)
        const daysDiff = Math.round((bookingDate.getTime() - currentSpan.end.getTime()) / (24 * 60 * 60 * 1000));
        if (daysDiff === 1) {
          // Extend current span
          currentSpan.end = bookingDate;
          currentSpan.bookings.push(familyBookings[i]);
        } else {
          // Gap found, save current span and start new one
          spans.push(currentSpan);
          currentSpan = {
            family: booking.family,
            start: bookingDate,
            end: bookingDate,
            bookings: [familyBookings[i]]
          };
        }
      }
    }
    
    // Don't forget the last span
    if (currentSpan) {
      spans.push(currentSpan);
    }

    // Find which span contains our target booking
    for (let span of spans) {
      if (span.bookings.some(b => b.id === bookingId)) {
        return {
          family: span.family,
          start: span.start,
          end: span.end
        };
      }
    }

    return null;
  };

  // Memoization cache for getAllBookingsForDate to prevent excessive calls
  const [bookingsCache, setBookingsCache] = useState(new Map());

  // Hämta alla bokningar för ett datum (för dubbelbokningar)
  const getAllBookingsForDate = (date) => {
    // Don't process if data hasn't loaded yet to avoid showing empty results
    if (!dataLoaded) {
      return [];
    }
    
    const dateStr = formatDate(date);
    
    // Check cache first to avoid redundant processing
    if (bookingsCache.has(dateStr)) {
      return bookingsCache.get(dateStr);
    }

    // FIRST: Filter bookings that actually have this specific date
    const bookingsForThisDate = bookings.filter(booking => booking.date === dateStr);

    // SECOND: Enhance each booking with span information and check-in/check-out status
    const result = bookingsForThisDate.map(booking => {
      const span = getBookingSpan(booking.id);

      if (!span) {
        return { ...booking, isCheckIn: false, isCheckOut: false, isMiddleDay: false };
      }

      // Fix: Säkerställ att datumjämförelser använder samma tidzon-format
      const checkInDate = span.start instanceof Date ? span.start : new Date(span.start + 'T00:00:00');
      const checkOutDate = span.end instanceof Date ? span.end : new Date(span.end + 'T00:00:00');
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);

      const isCheckIn = checkInDate.getTime() === targetDate.getTime();
      const isCheckOut = checkOutDate.getTime() === targetDate.getTime();
      const isMiddleDay = !isCheckIn && !isCheckOut && targetDate >= checkInDate && targetDate <= checkOutDate;

      // DEBUG: Log the calculation details
      console.log(`🔍 getAllBookingsForDate - Date: ${dateStr}, Booking: ${booking.family} (${booking.date})`);
      console.log(`  Check-in date: ${checkInDate.toDateString()}, Target: ${targetDate.toDateString()}, isCheckIn: ${isCheckIn}`);
      console.log(`  Check-out date: ${checkOutDate.toDateString()}, Target: ${targetDate.toDateString()}, isCheckOut: ${isCheckOut}`);
      console.log(`  isMiddleDay: ${isMiddleDay} (target >= checkIn: ${targetDate >= checkInDate}, target <= checkOut: ${targetDate <= checkOutDate})`);

      return { ...booking, isCheckIn, isCheckOut, isMiddleDay, startDate: formatDate(span.start), endDate: formatDate(span.end) };
    });

    // Cache the result to avoid recomputation
    setBookingsCache(prev => new Map(prev.set(dateStr, result)));
    
    return result;
  };

  const getBookingsForDate = (date) => {
    const formattedDate = formatDate(date);
    return bookings.filter(booking => booking.date === formattedDate);
  };

  const getOtherBookingsForDate = (booking) => {
    if (!booking || !booking.date) return [];
    return bookings.filter(b => 
      b.id !== booking.id && 
      b.date === (booking.date instanceof Date ? formatDate(booking.date) : booking.date)
    );
  };

  const isDateBooked = (date) => {
    return !!getBookingForDate(date);
  };
  
  // NEW LOGIC: Calculate booking types on-the-fly without relying on cached properties
  const isDateBookedByOther = (date) => {
    const dateStr = formatDate(date);
    const allBookings = bookings.filter(booking => booking.date === dateStr);
    if (allBookings.length === 0) return false;
    if (!selectedFamily) return allBookings.length > 0;

    // If selected family already has a booking on this date, allow it
    const familyAlreadyBooked = allBookings.some(b => b.family === selectedFamily);
    if (familyAlreadyBooked) {
      console.log(`✅ isDateBookedByOther - Same selected family already booked, allowing`);
      return false;
    }

    // Get bookings from other families
    const otherFamilyBookings = allBookings.filter(b => b.family !== selectedFamily);

    if (otherFamilyBookings.length === 0) {
      console.log(`✅ isDateBookedByOther - No other families booked, allowing`);
      return false;
    }

    console.log(`🔍 isDateBookedByOther - Date: ${dateStr}, Other families:`, otherFamilyBookings.map(b => b.family));

    // Calculate booking types on-the-fly for each other family booking
    const bookingTypes = otherFamilyBookings.map(booking => {
      const span = getBookingSpan(booking.id);
      if (!span) {
        return { family: booking.family, type: 'unknown' };
      }

      const checkInDate = span.start instanceof Date ? span.start : new Date(span.start + 'T00:00:00');
      const checkOutDate = span.end instanceof Date ? span.end : new Date(span.end + 'T00:00:00');
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0);

      const isCheckIn = checkInDate.getTime() === targetDate.getTime();
      const isCheckOut = checkOutDate.getTime() === targetDate.getTime();
      const isMiddleDay = !isCheckIn && !isCheckOut && targetDate >= checkInDate && targetDate <= checkOutDate;

      return {
        family: booking.family,
        type: isCheckIn ? 'checkIn' : isCheckOut ? 'checkOut' : isMiddleDay ? 'middleDay' : 'unknown'
      };
    });

    console.log(`🔍 isDateBookedByOther - Booking types:`, bookingTypes);

    // Check if there are any middle day bookings from other families
    const hasMiddleDayBookings = bookingTypes.some(bt => bt.type === 'middleDay');

    // Check if this is a transition day (has check-in or check-out bookings)
    const hasTransitionBookings = bookingTypes.some(bt => bt.type === 'checkIn' || bt.type === 'checkOut');

    console.log(`🔄 isDateBookedByOther - Analysis: hasMiddleDayBookings=${hasMiddleDayBookings}, hasTransitionBookings=${hasTransitionBookings}`);

    // If there are middle day bookings from other families, BLOCK
    if (hasMiddleDayBookings) {
      console.log(`🚫 isDateBookedByOther - BLOCKED: Middle day already occupied by other families`);
      return true;
    }

    // If this is a transition day, ALLOW double booking
    if (hasTransitionBookings) {
      console.log(`🔄 isDateBookedByOther - Transition day detected - allowing double booking`);
      return false;
    }

    // If we get here, there are other family bookings but they're not middle days and not transition days
    // This shouldn't happen, but if it does, be safe and block
    console.log(`🚫 isDateBookedByOther - BLOCKED: Other families booked (unknown booking type)`);
    return true;
  };

  // DEBUG VERSION: Let's create a simple version that just checks for any other family bookings
  const isDateBookedByOtherDebug = (date) => {
    const dateStr = formatDate(date);
    const allBookings = bookings.filter(booking => booking.date === dateStr);
    if (allBookings.length === 0) return false;
    if (!selectedFamily) return allBookings.length > 0;

    // If selected family already has a booking on this date, allow it
    const familyAlreadyBooked = allBookings.some(b => b.family === selectedFamily);
    if (familyAlreadyBooked) {
      console.log(`✅ isDateBookedByOtherDebug - Same selected family already booked, allowing`);
      return false;
    }

    // Get bookings from other families
    const otherFamilyBookings = allBookings.filter(b => b.family !== selectedFamily);

    if (otherFamilyBookings.length === 0) {
      console.log(`✅ isDateBookedByOtherDebug - No other families booked, allowing`);
      return false;
    }

    console.log(`🔍 isDateBookedByOtherDebug - Date: ${dateStr}, Other families:`, otherFamilyBookings.map(b => b.family));
    console.log(`🔍 isDateBookedByOtherDebug - Other family bookings details:`, otherFamilyBookings.map(b => ({
      family: b.family,
      isCheckIn: b.isCheckIn,
      isCheckOut: b.isCheckOut,
      isMiddleDay: b.isMiddleDay
    })));

    // For now, let's be more permissive and only block if there are middle day bookings
    const hasMiddleDayBookings = otherFamilyBookings.some(booking => {
      return booking.isMiddleDay;
    });

    if (hasMiddleDayBookings) {
      console.log(`🚫 isDateBookedByOtherDebug - BLOCKED: Middle day bookings found`);
      return true;
    }

    // Allow all other cases (including transition days)
    console.log(`✅ isDateBookedByOtherDebug - ALLOWED: No middle day conflicts`);
    return false;
  };

  // Simple version that blocks ALL double bookings except same family
  const isDateBookedByOtherSimple = (date) => {
    const dateStr = formatDate(date);
    const allBookings = bookings.filter(booking => booking.date === dateStr);
    if (allBookings.length === 0) return false;
    if (!selectedFamily) return allBookings.length > 0;

    // If selected family already has a booking on this date, allow it
    const familyAlreadyBooked = allBookings.some(b => b.family === selectedFamily);
    if (familyAlreadyBooked) {
      console.log(`✅ isDateBookedByOtherSimple - Same selected family already booked, allowing`);
      return false;
    }

    // If ANY other family has a booking on this date, block it
    const otherFamilies = allBookings.filter(b => b.family !== selectedFamily);
    if (otherFamilies.length > 0) {
      console.log(`🚫 isDateBookedByOtherSimple - BLOCKED: Other families already booked on this date:`, otherFamilies.map(b => b.family));
      return true;
    }

    return false;
  };

  // Stricter version that blocks ALL double bookings except for exact same family
  const isDateBookedByOtherStrict = (date) => {
    const dateStr = formatDate(date);
    const allBookings = bookings.filter(booking => booking.date === dateStr);
    if (allBookings.length === 0) return false;
    if (!selectedFamily) return allBookings.length > 0;

    // If selected family already has a booking on this date, allow it
    const familyAlreadyBooked = allBookings.some(b => b.family === selectedFamily);
    if (familyAlreadyBooked) {
      console.log(`✅ isDateBookedByOtherStrict - Same selected family already booked, allowing`);
      return false;
    }

    // If ANY other family has a booking on this date, block it
    const otherFamilies = allBookings.filter(b => b.family !== selectedFamily);
    if (otherFamilies.length > 0) {
      console.log(`🚫 isDateBookedByOtherStrict - BLOCKED: Other families already booked on this date:`, otherFamilies.map(b => b.family));
      return true;
    }

    return false;
  };

  // Kontrollera om ett datum är första eller sista dagen av en bokning
  const isFirstOrLastDayOfBooking = (date, family) => {
    const familyBookings = bookings.filter(b => b.family === family);
    if (familyBookings.length === 0) return false;
    
    // Gruppera datum per familj och hitta datumspann
    const dates = familyBookings.map(b => new Date(b.date)).sort((a, b) => a - b);
    const spans = [];
    
    if (dates.length > 0) {
      let currentSpan = { start: dates[0], end: dates[0] };
      
      for (let i = 1; i < dates.length; i++) {
        const prevDate = dates[i - 1];
        const currentDate = dates[i];
        const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
        
        if (diffDays <= 1) {
          // Konsekutiva datum, utöka spannet
          currentSpan.end = currentDate;
        } else {
          // Nytt spann
          spans.push(currentSpan);
          currentSpan = { start: currentDate, end: currentDate };
        }
      }
      spans.push(currentSpan);
    }
    
    // Kontrollera om datumet är första eller sista dagen i något spann
    const targetDate = new Date(date);
    return spans.some(span => {
      const isFirstDay = span.start.toDateString() === targetDate.toDateString();
      const isLastDay = span.end.toDateString() === targetDate.toDateString();
      return isFirstDay || isLastDay;
    });
  };
  
  // Hjälpare: är datumet inom det valda spannet?
  const isInSelectedRange = (date) => {
    if (!Array.isArray(selectedDates) || selectedDates.length !== 2) return false;
    if (!date || !selectedDates[0] || !selectedDates[1]) return false;
    const [start, end] = selectedDates;
    // Normalisera till YYYY-MM-DD för jämförelse
    const toYMD = (d) => {
      // Guard clause: ensure date is valid
      if (!d || !(d instanceof Date) || isNaN(d.getTime())) return '';
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const dStr = toYMD(date);
    const sStr = toYMD(start);
    const eStr = toYMD(end);
    // Additional guard: if any date conversion failed, return false
    if (!dStr || !sStr || !eStr) return false;
    return dStr >= sStr && dStr <= eStr;
  };

  // Hantera datumval i kalendern - använd onClickDay för bättre kontroll
  const handleDateClick = (clickedDate) => {
    console.log('Date clicked:', clickedDate, 'Current selection:', selectedDates, 'Click count:', clickCount);
    
    // Allow all date clicks - let backend handle conflicts
    console.log('Allowing date click, backend will handle conflicts');

    if (clickCount === 0) {
      // Första klicket - sätt startdatum
      setSelectedDates([clickedDate]);
      setClickCount(1);
    } else if (clickCount === 1) {
      // Andra klicket - sätt slutdatum eller börja om
      const firstDate = selectedDates[0];
      if (clickedDate < firstDate) {
        // Klickade på ett tidigare datum - börja om
        setSelectedDates([clickedDate]);
        setClickCount(1);
      } else {
        // Allow range selection - backend will validate conflicts
        const dateRange = [];
        const current = new Date(firstDate);
        while (current <= clickedDate) {
          dateRange.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        
        // Don't check for conflicts in frontend - let backend handle it
        const hasConflictsInRange = false;
        if (hasConflictsInRange) {
          console.log('Range contains conflicting dates, starting new selection');
          setSelectedDates([clickedDate]);
          setClickCount(1);
          return;
        }
        
        // Sätt slutdatum
        setSelectedDates([firstDate, clickedDate]);
        setClickCount(2);
      }
    } else {
      // Tredje klicket - börja om
      setSelectedDates([clickedDate]);
      setClickCount(1);
    }
  };

  // Expand selection to full booking span only for display when user clicks "Visa"
  const adjustSelectionForShow = () => {
    if (!Array.isArray(selectedDates) || selectedDates.length !== 1) return;
    const d = selectedDates[0];
    const dateStr = formatDate(d);
    const clickedBookings = bookings.filter(booking => booking.date === dateStr);
    if (clickedBookings.length === 0) return;
    const firstBooking = clickedBookings[0];
    const familyBookings = bookings.filter(b => b.family === firstBooking.family);
    const familySpans = groupBookings(familyBookings);
    const clickedDateStr = formatDate(d);
    const containingSpan = familySpans.find(span => clickedDateStr >= span.start && clickedDateStr <= span.end);
    if (containingSpan) {
      const startDate = new Date(containingSpan.start + 'T00:00:00');
      const endDate = new Date(containingSpan.end + 'T00:00:00');
      setSelectedDates([startDate, endDate]);
    }
  };

  // Hantera Calendar onChange - bara för att synka med Calendar-komponenten
  const handleDateSelection = (value) => {
    // Låt onClickDay hantera logiken istället
  };

  const [bookings, setBookings] = useState([]);

  const groupBookings = (bookings) => {
    if (!bookings || bookings.length === 0) return [];

    // 1. Gruppera bokningar per familj
    const bookingsByFamily = bookings.reduce((acc, booking) => {
      acc[booking.family] = acc[booking.family] || [];
      acc[booking.family].push(new Date(booking.date + 'T00:00:00'));
      return acc;
    }, {});

    // 2. Skapa datumspann för varje familj
    const allSpans = [];
    for (const family in bookingsByFamily) {
      const dates = bookingsByFamily[family].sort((a, b) => a - b);
      if (dates.length > 0) {
        let currentSpan = { family, start: formatDate(dates[0]), end: formatDate(dates[0]) };
        for (let i = 1; i < dates.length; i++) {
          const prevDate = dates[i - 1];
          const currentDate = dates[i];
          const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);

          if (diffDays === 1) {
            currentSpan.end = formatDate(currentDate); // Utöka spannet
          } else {
            allSpans.push(currentSpan); // Avsluta föregående spann
            currentSpan = { family, start: formatDate(currentDate), end: formatDate(currentDate) }; // Starta ett nytt
          }
        }
        allSpans.push(currentSpan); // Lägg till det sista spannet
      }
    }

    // 3. Sortera alla spann efter startdatum
    return allSpans.sort((a, b) => new Date(a.start) - new Date(b.start));
  };

  const [message, setMessage] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingComment, setBookingComment] = useState('');
  const [comment, setComment] = useState('');
  const [showRangeModal, setShowRangeModal] = useState(false);
  const [showFamilyBillModal, setShowFamilyBillModal] = useState(false);
  const [selectedFamilyBill, setSelectedFamilyBill] = useState(null);
  const [showPaidBookings, setShowPaidBookings] = useState({}); // Track which families show paid bookings
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  // Simple hash-based routing: '#/booking' and '#/billing'
  const [route, setRoute] = useState(() => window.location.hash || '#/booking');
  const [paymentHistory, setPaymentHistory] = useState(() => {
    // Load payment history from localStorage
    try {
      const stored = localStorage.getItem('paymentHistory');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState(null);
  const [showSwishQR, setShowSwishQR] = useState(false);
  const currentYear = new Date().getFullYear();
  // Families that should be hidden after being fully settled (remainingCost === 0)
  const [hiddenFamilies, setHiddenFamilies] = useState(new Set());
  const [hideCountdowns, setHideCountdowns] = useState({}); // family -> seconds remaining
  const hideTimersRef = useRef({});    // family -> timeout id
  const hideIntervalsRef = useRef({}); // family -> interval id
  
  // Clear all data function
  const clearAllData = () => {
    setPaymentHistory([]);
    setPaidBookings(new Set());
    setBookings([]);
    setElectricityUsage({});
    localStorage.removeItem('paymentHistory');
    localStorage.removeItem('mockBookings'); // Fix: consistent key usage
    localStorage.removeItem('electricityUsage');
    localStorage.removeItem('paidBookings');
    alert('All data cleared!');
  };

  // Billing constants
  const COST_PER_NIGHT = CONFIG.NIGHT_COST;
  const COST_PER_KWH = CONFIG.ELECTRICITY_RATE;

  // Calculate nights from booking spans, only for dates that have passed
  const calculateNights = (bookingSpans) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of today
    
    return bookingSpans.reduce((total, span) => {
      // Normalize span dates to Date objects (strings are in YYYY-MM-DD)
      const spanStart = span.start instanceof Date ? new Date(span.start) : new Date(span.start + 'T00:00:00');
      const spanEnd = span.end instanceof Date ? new Date(span.end) : new Date(span.end + 'T00:00:00');
      
      // Only count nights up to today
      const cappedEnd = spanEnd < today ? spanEnd : today;
      
      // If the entire span is in the future, don't count any nights
      if (spanStart >= today) {
        return total;
      }
      
      const nights = Math.ceil((cappedEnd - spanStart) / (1000 * 60 * 60 * 24));
      return total + Math.max(nights, 0); // Can be 0 if no nights have passed yet
    }, 0);
  };

  // Get billing summary per family
  const getBillingSummary = (electricityUsageOverride) => {
    const usageMap = electricityUsageOverride ?? electricityUsage;
    const familyBills = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    console.log('getBillingSummary - families:', families);
    console.log('getBillingSummary - bookings:', bookings);
    
    families.forEach(familyName => {
      const familyBookings = bookings.filter(b => b.family === familyName);
      console.log(`Family ${familyName} bookings:`, familyBookings);
      const bookingSpans = groupBookings(familyBookings);
      const nights = calculateNights(bookingSpans);
      const nightsCost = nights * COST_PER_NIGHT;

      // Calculate electricity costs for checkout days
      const electricityCostRaw = familyBookings.reduce((sum, booking) => {
        const bookingDate = new Date(booking.date);
        bookingDate.setHours(0, 0, 0, 0);
        
        // Allow electricity to be entered/charged already on checkout day (today)
        if (bookingDate > today) return sum;
        
        for (const span of bookingSpans) {
          const spanEndDate = span.end instanceof Date ? new Date(span.end) : new Date(span.end + 'T00:00:00');
          spanEndDate.setHours(0, 0, 0, 0);
          if (bookingDate.getTime() === spanEndDate.getTime()) {
            const kwh = parseFloat(usageMap[booking.id]) || 0;
            return sum + (kwh * COST_PER_KWH);
          }
        }
        return sum;
      }, 0);
      const electricityCost = Math.round(electricityCostRaw);
      
      const totalCost = nightsCost + electricityCost;
      
      // Only count payments for bookings that have already occurred and are NOT checkout days
      const paidAmount = familyBookings.reduce((sum, booking) => {
        const bookingDate = new Date(booking.date);
        bookingDate.setHours(0, 0, 0, 0);
        
        // Only count payment if the booking date has passed and it's marked as paid
        if (bookingDate < today && paidBookings.has(booking.id)) {
          // Exclude checkout days from night payment count
          for (const span of bookingSpans) {
            const spanEndDate = span.end instanceof Date ? new Date(span.end) : new Date(span.end + 'T00:00:00');
            spanEndDate.setHours(0, 0, 0, 0);
            if (bookingDate.getTime() === spanEndDate.getTime()) {
              return sum; // skip checkout day
            }
          }
          return sum + COST_PER_NIGHT;
        }
        return sum;
      }, 0);
      
      // Add paid electricity costs to paidAmount
      const paidElectricityCostRaw = familyBookings.reduce((sum, booking) => {
        const bookingDate = new Date(booking.date);
        bookingDate.setHours(0, 0, 0, 0);
        
        // Only include past bookings
        if (bookingDate >= today) return sum;
        
        // Check if this is a checkout day (last day of span) and if it's paid
        for (const span of bookingSpans) {
          const spanEndDate = span.end instanceof Date ? new Date(span.end) : new Date(span.end + 'T00:00:00');
          spanEndDate.setHours(0, 0, 0, 0);
          if (bookingDate.getTime() === spanEndDate.getTime() && paidBookings.has(booking.id)) {
            const kwh = parseFloat(electricityUsage[booking.id]) || 0;
            return sum + (kwh * COST_PER_KWH);
          }
        }
        return sum;
      }, 0);
      const paidElectricityCost = Math.round(paidElectricityCostRaw);
      
      const totalPaidAmount = paidAmount + paidElectricityCost;
      
      // Skip families that have no costs and no payments to show
      // This prevents listing families that are not owing anything and have no relevant history
      if (totalCost === 0 && totalPaidAmount === 0) {
        return; // continue to next family
      }
      
      // Always include family in billing, even if no costs yet
      const billableBookings = familyBookings.filter(b => {
        const bookingDate = new Date(b.date);
        bookingDate.setHours(0, 0, 0, 0);
        
        // Only show past bookings for billing
        if (bookingDate >= today) return false;
        
        // Check if this booking is the last day of any span
        for (const span of bookingSpans) {
          const spanEndDate = new Date(span.end);
          spanEndDate.setHours(0, 0, 0, 0);
          if (bookingDate.getTime() === spanEndDate.getTime()) {
            return false; // This is a checkout day, exclude from billing
          }
        }
        return true;
      });
      
      console.log(`Family ${familyName} - nights: ${nights}, electricityCost: ${electricityCost}, totalCost: ${totalCost}`);
      
      familyBills[familyName] = {
        family: familyName,
        nights,
        nightsCost,
        electricityCost,
        totalCost,
        paidAmount: totalPaidAmount,
        remainingCost: Math.max(totalCost - totalPaidAmount, 0),
        bookingSpans,
        bookings: billableBookings
      };
    });
    
    console.log('Final familyBills:', familyBills);
    return familyBills;
  };

  // Toggle paid status for a booking
  const togglePaidStatus = (bookingId) => {
    const newPaidBookings = new Set(paidBookings);
    const booking = bookings.find(b => b.id === bookingId);
    
    if (newPaidBookings.has(bookingId)) {
      newPaidBookings.delete(bookingId);
      // Remove from payment history
      setPaymentHistory(prev => {
        const updated = prev.filter(p => p.bookingId !== bookingId);
        localStorage.setItem('paymentHistory', JSON.stringify(updated));
        return updated;
      });
    } else {
      newPaidBookings.add(bookingId);
      // Add to payment history
      const payment = {
        id: Date.now(),
        bookingId,
        family: booking.family,
        date: booking.date,
        amount: COST_PER_NIGHT,
        type: 'accommodation',
        paidAt: new Date().toISOString(),
        actionLog: [{
          action: 'payment_confirmed',
          method: 'manual',
          timestamp: new Date().toISOString(),
          note: 'Bokning markerad som betald manuellt'
        }]
      };
      setPaymentHistory(prev => {
        const updated = [...prev, payment];
        localStorage.setItem('paymentHistory', JSON.stringify(updated));
        return updated;
      });
    }
    setPaidBookings(newPaidBookings);
    // Save paid bookings to localStorage
    localStorage.setItem('paidBookings', JSON.stringify([...newPaidBookings]));
  };

  // Pay all unpaid bookings for a family
  const payAllForFamily = (allFamilyBookings, familyName, paymentMethod = 'manual') => {
    const newPaidBookings = new Set(paidBookings);
    const newPayments = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Get ALL family bookings (not just billable ones) to calculate spans correctly
    const familyBookings = bookings.filter(b => b.family === familyName);
    const familySpans = groupBookings(familyBookings);
    
    // Pay for all unpaid night bookings (excluding checkout days)
    allFamilyBookings.forEach(booking => {
      if (!newPaidBookings.has(booking.id)) {
        newPaidBookings.add(booking.id);
        newPayments.push({
          bookingId: booking.id,
          family: booking.family,
          date: booking.date,
          amount: COST_PER_NIGHT,
          type: 'night',
          paidAt: new Date().toISOString(),
          actionLog: [{
            action: 'payment_confirmed',
            method: paymentMethod === 'swish' ? 'swish' : 'manual',
            timestamp: new Date().toISOString(),
            note: paymentMethod === 'swish' ? 'Betald via Swish' : 'Markerad som betald manuellt'
          }]
        });
      }
    });
    
    // Add electricity costs for checkout days that have kWh usage
    familyBookings.forEach(booking => {
      const bookingDate = new Date(booking.date);
      bookingDate.setHours(0, 0, 0, 0);
      
      // Only include past bookings
      if (bookingDate >= today) return;
      
      // Check if this is a checkout day (last day of span)
      for (const span of familySpans) {
        const spanEndDate = new Date(span.end);
        spanEndDate.setHours(0, 0, 0, 0);
        if (bookingDate.getTime() === spanEndDate.getTime()) {
          const kwh = parseFloat(electricityUsage[booking.id]) || 0;
          if (kwh > 0) {
            const electricityCost = Math.round(kwh * COST_PER_KWH);
            // Mark the booking as paid for electricity too
            newPaidBookings.add(booking.id);
            newPayments.push({
              id: Date.now() + Math.random(),
              bookingId: booking.id,
              family: booking.family,
              date: booking.date,
              amount: electricityCost,
              kwh: parseFloat(electricityUsage[booking.id]) || 0,
              type: 'electricity',
              paidAt: new Date().toISOString(),
              actionLog: [{
                action: 'payment_confirmed',
                method: paymentMethod === 'swish' ? 'swish' : 'manual',
                timestamp: new Date().toISOString(),
                note: paymentMethod === 'swish' ? 'Elkostnad betald via Swish' : 'Elkostnad markerad som betald manuellt'
              }]
            });
          }
          break;
        }
      }
    });
    
    setPaidBookings(newPaidBookings);
    // Save paid bookings to localStorage
    localStorage.setItem('paidBookings', JSON.stringify([...newPaidBookings]));
    setPaymentHistory(prev => {
      const updated = [...prev, ...newPayments];
      localStorage.setItem('paymentHistory', JSON.stringify(updated));
      return updated;
    });
  };

  // Handle undoing a payment
  const handleUndoPayment = (paymentId) => {
    setPaymentHistory(prev => {
      const paymentToUndo = prev.find(p => p.id === paymentId);
      if (!paymentToUndo) return prev;

      // Find all payments for the same family and date span that were paid together
      const relatedPayments = prev.filter(p => 
        p.family === paymentToUndo.family && 
        p.type === paymentToUndo.type &&
        Math.abs(new Date(p.paidAt).getTime() - new Date(paymentToUndo.paidAt).getTime()) < 5000 // Within 5 seconds
      );

      // Remove all related payments from history
      const updated = prev.filter(p => !relatedPayments.some(rp => rp.id === p.id));
      localStorage.setItem('paymentHistory', JSON.stringify(updated));

      // Remove all related bookings from paidBookings
      setPaidBookings(prevPaid => {
        const newPaid = new Set(prevPaid);
        relatedPayments.forEach(payment => {
          if (payment.bookingId) {
            newPaid.delete(payment.bookingId);
          }
        });
        localStorage.setItem('paidBookings', JSON.stringify([...newPaid]));
        return newPaid;
      });

      const totalAmount = relatedPayments.reduce((sum, p) => sum + p.amount, 0);
      setMessage(`${relatedPayments.length} betalning${relatedPayments.length > 1 ? 'ar' : ''} på totalt ${formatKr(totalAmount)} kr har ångrats`);
      return updated;
    });
  };

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API}/bookings`);
      const ct = response.headers.get('content-type') || '';
      if (!response.ok) {
        // If Access or routing intercepts, we may get HTML or 403
        throw new Error(`HTTP ${response.status}`);
      }
      if (!ct.includes('application/json')) {
        // Likely intercepted by Cloudflare Access or served an HTML page
        throw new Error('NON_JSON_RESPONSE');
      }
      const data = await response.json();
      console.log(`📊 Fetched ${data.length} bookings from API`);
      
      setBookings(Array.isArray(data) ? data : []);
      setBookingsCache(new Map()); // Clear cache when new data is loaded
      setDataLoaded(true); // Mark data as loaded
    } catch (error) {
      console.error('Fel vid hämtning av bokningar:', error);
      // Graceful fallback to local mock if running on a protected preview
      if (!isLocalDevelopment) {
        console.warn('Aktiverar lokal fallback för bokningar (skyddad förhandsvisning eller icke-JSON svar)');
        setUseFallbackMock(true);
        try {
          const saved = localStorage.getItem('mockBookings');
          const fallbackData = saved ? JSON.parse(saved) : mockBookings;
          console.log(`📊 Using fallback mock data: ${fallbackData.length} bookings`);
          
          setBookings(fallbackData);
          setBookingsCache(new Map()); // Clear cache when fallback data is loaded
        } catch {
          console.log(`📊 Using empty mock bookings array`);
          setBookings(mockBookings);
        }
      }
      setDataLoaded(true); // Mark data as loaded even on error
    }
  };

  const fetchFamilies = async () => {
    try {
      const response = await fetch(`${API}/families`);
      const ct = response.headers.get('content-type') || '';
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      if (!ct.includes('application/json')) {
        throw new Error('NON_JSON_RESPONSE');
      }
      const data = await response.json();
      setFamilies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fel vid hämtning av familjer:', error);
      if (!isLocalDevelopment) {
        console.warn('Aktiverar lokal fallback för familjer (skyddad förhandsvisning eller icke-JSON svar)');
        setUseFallbackMock(true);
        setFamilies(mockFamilies);
      }
    }
  };

  // Hämta familjer och bokningar efter inloggning
  useEffect(() => {
    if (step === 1) {
      if (isLocalDevelopment || useFallbackMock) {
        // Use mock data for local development
        setFamilies(mockFamilies);
        console.log(`📊 Local development: Loading ${mockBookings.length} mock bookings`);
        
        setBookings(mockBookings);
        setBookingsCache(new Map()); // Clear cache when mock data is loaded
        setDataLoaded(true); // Mark data as loaded for development
        
        // Load paid bookings from localStorage
        const savedPaidBookings = localStorage.getItem('paidBookings');
        if (savedPaidBookings) {
          setPaidBookings(new Set(JSON.parse(savedPaidBookings)));
        }
        
        // Load electricity usage from localStorage
        const savedElectricityUsage = localStorage.getItem('electricityUsage');
        if (savedElectricityUsage) {
          setElectricityUsage(JSON.parse(savedElectricityUsage));
        }
        
        // Load payment history from localStorage
        const savedPaymentHistory = localStorage.getItem('paymentHistory');
        if (savedPaymentHistory) {
          setPaymentHistory(JSON.parse(savedPaymentHistory));
        }
      } else {
        // Fetch real data from API
        fetchFamilies();
        fetchBookings();
        // Also restore client-side persisted state in production
        try {
          const savedPaidBookings = localStorage.getItem('paidBookings');
          if (savedPaidBookings) {
            setPaidBookings(new Set(JSON.parse(savedPaidBookings)));
          }
        } catch {}
        try {
          const savedElectricityUsage = localStorage.getItem('electricityUsage');
          if (savedElectricityUsage) {
            setElectricityUsage(JSON.parse(savedElectricityUsage));
          }
        } catch {}
        try {
          const savedPaymentHistory = localStorage.getItem('paymentHistory');
          if (savedPaymentHistory) {
            setPaymentHistory(JSON.parse(savedPaymentHistory));
          }
        } catch {}
      }
    }
  }, [step]);

  // Clear payment history when bookings change (to keep data consistent)
  useEffect(() => {
    if (bookings.length === 0) {
      // If no bookings, clear all payment-related data
      setPaymentHistory([]);
      setPaidBookings(new Set());
      setElectricityUsage({});
      localStorage.removeItem('paymentHistory');
      localStorage.removeItem('paidBookings');
      localStorage.removeItem('electricityUsage');
    }
  }, [bookings]);

  // Persist electricity usage whenever it changes (covers manual input case)
  useEffect(() => {
    try {
      localStorage.setItem('electricityUsage', JSON.stringify(electricityUsage));
    } catch {}
  }, [electricityUsage]);

  // Cleanup any pending hide timers on unmount
  useEffect(() => {
    return () => {
      const timers = hideTimersRef.current || {};
      Object.values(timers).forEach((id) => clearTimeout(id));
      const intervals = hideIntervalsRef.current || {};
      Object.values(intervals).forEach((id) => clearInterval(id));
    };
  }, []);

  // Listen to hash changes to update route
  useEffect(() => {
    const onHashChange = () => setRoute(window.location.hash || '#/booking');
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Auto-hide families ~5s after their remainingCost becomes 0
  useEffect(() => {
    // Build current billing snapshot
    const billing = getBillingSummary();
    const timers = hideTimersRef.current || {};
    const intervals = hideIntervalsRef.current || {};

    Object.values(billing).forEach((bill) => {
      const fam = bill.family;
      if (bill.remainingCost === 0) {
        // Schedule hide if not already hidden and no timer pending
        if (!hiddenFamilies.has(fam) && !timers[fam]) {
          // start countdown at 10s
          setHideCountdowns((prev) => ({ ...prev, [fam]: 10 }));
          // tick every second
          intervals[fam] = setInterval(() => {
            setHideCountdowns((prev) => {
              const current = prev[fam] ?? 0;
              const nextVal = Math.max(current - 1, 0);
              return { ...prev, [fam]: nextVal };
            });
          }, 1000);
          // final hide after 10s
          timers[fam] = setTimeout(() => {
            setHiddenFamilies((prev) => {
              const next = new Set(prev);
              next.add(fam);
              return next;
            });
            // clear interval and cleanup countdown state
            if (intervals[fam]) {
              clearInterval(intervals[fam]);
              delete intervals[fam];
            }
            setHideCountdowns((prev) => {
              const { [fam]: _, ...rest } = prev;
              return rest;
            });
            delete timers[fam];
          }, 10000);
        }
      } else {
        // They owe again: ensure visible and clear any pending timer
        if (hiddenFamilies.has(fam)) {
          setHiddenFamilies((prev) => {
            const next = new Set(prev);
            next.delete(fam);
            return next;
          });
        }
        if (timers[fam]) {
          clearTimeout(timers[fam]);
          delete timers[fam];
        }
        if (intervals[fam]) {
          clearInterval(intervals[fam]);
          delete intervals[fam];
        }
        // remove any countdown badge
        setHideCountdowns((prev) => {
          if (prev[fam] === undefined) return prev;
          const { [fam]: _, ...rest } = prev;
          return rest;
        });
      }
    });
  }, [bookings, paidBookings, electricityUsage, families]);



  // Logga in med kod
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (isLocalDevelopment) {
      // Use mock API for local development
      const data = await mockLogin(code);
      if (data.success) {
        setStep(1);
        setMessage("");
      } else {
        setMessage(data.message || "Fel kod. Försök igen!");
      }
    } else {
      // Use real API for production
      try {
        console.log('Logging in to API:', `${API}/login`);
        const res = await fetch(`${API}/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code })
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.error('Login failed - status:', res.status, 'body:', text);
          setMessage(res.status === 405 ? 'Inloggning blockerad (405). Prova ladda om sidan.' : 'Inloggning misslyckades. Försök igen.');
          return;
        }
        let data;
        try {
          data = await res.json();
        } catch (err) {
          const text = await res.text().catch(() => '');
          console.error('Could not parse JSON from login response. Raw:', text);
          setMessage('Oväntat svar från servern vid inloggning. Försök igen.');
          return;
        }
        if (data && data.success) {
          setStep(1);
          setMessage("");
        } else {
          setMessage(data?.message || "Fel kod. Försök igen!");
        }
      } catch (error) {
        console.error('Login error:', error);
        setMessage('Kunde inte kontakta servern. Kontrollera uppkopplingen och försök igen.');
      }
    }
  };

  // Hämta alla datum i nu vald range som Date[]
  const getSelectedDatesArray = () => {
    if (!Array.isArray(selectedDates) || selectedDates.length === 0) return [];
    
    if (selectedDates.length === 1) {
      // Enskilt datum - returnera bara det datumet
      return [new Date(selectedDates[0])];
    }
    
    if (selectedDates.length === 2) {
      // Intervall - returnera alla datum mellan start och slut
      const [start, end] = selectedDates;
      const out = [];
      let d = new Date(start);
      while (d <= end) {
        out.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
      return out;
    }
    
    return [];
  };

  // Uppdatera en befintlig bokning
  const handleUpdateBooking = async (e) => {
    e.preventDefault();
    try {
      // Skapa en kopia av redigeringsobjektet med rätt datumformat
      const updatedBooking = {
        ...editingBooking,
        date: editingBooking.date instanceof Date ? formatDate(editingBooking.date) : editingBooking.date,
        comment: comment
      };

      if (useMockData) {
        // Mock update using mock function
        // ...existing code for mock update...
        // (If you want to implement mock update logic, do it here)
        return;
      }
      // ...existing code for real update...
      // ...existing code...
      setMessage(userMessage);
    } catch (err) {
      setMessage('Ett fel uppstod vid uppdatering av bokning.');
    }

      const otherFailures = results.filter(r => !r.ok && r.status !== 409);

      console.log(`Results summary: ${successfulBookings.length} successful, ${alreadyBooked.length} already booked, ${maxFamiliesExceeded.length} max families exceeded, ${otherConflicts.length} other conflicts, ${otherFailures.length} other failures`);

      if (otherFailures.length > 0) {
        console.error('Non-conflict failures:', otherFailures);
        setMessage(`${otherFailures.length} av ${results.length} bokningar misslyckades (tekniskt fel)`);
        return;
      }

      // Build detailed user message
      let userMessage = '';

      if (successfulBookings.length > 0) {
        userMessage += `${successfulBookings.length} dagar bokade.`;
      }

      const conflictCount = alreadyBooked.length + maxFamiliesExceeded.length + otherConflicts.length;
      if (conflictCount > 0) {
        const conflictDetails = [];
        if (alreadyBooked.length > 0) {
          conflictDetails.push(`${alreadyBooked.length} dagar redan bokade av familjen`);
        }
        if (maxFamiliesExceeded.length > 0) {
          conflictDetails.push(`${maxFamiliesExceeded.length} dagar översteg max antal familjer`);
        }
        if (otherConflicts.length > 0) {
          conflictDetails.push(`${otherConflicts.length} andra konflikter`);
        }
        userMessage += ` ${conflictCount} hade konflikter (${conflictDetails.join(', ')}).`;
      }

      if (userMessage === '') {
        userMessage = 'Inga bokningar kunde genomföras.';
      }

      setMessage(userMessage);
  };

  // Hantera datumändring vid redigering
  const handleDateChange = (date) => {
    setEditingBooking({
      ...editingBooking,
      date: date
    });
  };

  // Ta bort en bokning
  const handleDeleteBooking = async (id) => {
    console.log('🔍 handleDeleteBooking called with id:', id);
    console.log('🔍 Current bookings before deletion:', bookings.length);
    console.log('🔍 Current bookings:', bookings.map(b => ({id: b.id, family: b.family, date: b.date})));

    // Check if booking is paid
    if (paidBookings.has(id)) {
      alert('Du kan inte ta bort en betald bokning. Ångra betalningen först i "Tidigare betalningar".');
      return;
    }

    if (window.confirm('Är du säker på att du vill ta bort denna bokning?')) {
      if (useMockData) {
        // Mock deletion - properly update state and localStorage
        try {
          console.log('🔍 Starting mock deletion for booking id:', id);

          // Delete from localStorage using mock function
          await mockDeleteBooking(id);

          console.log('🔍 Mock deletion completed, updating state...');

          // Immediately update state to reflect the change
          const updatedBookings = bookings.filter(b => b.id !== id);
          console.log('🔍 Updated bookings after deletion:', updatedBookings.length);
          console.log('🔍 Updated bookings:', updatedBookings.map(b => ({id: b.id, family: b.family, date: b.date})));

          setBookings(updatedBookings);

          // Also update paid bookings if this booking was marked as paid
          const newPaidBookings = new Set(paidBookings);
          newPaidBookings.delete(id);
          setPaidBookings(newPaidBookings);
          localStorage.setItem('paidBookings', JSON.stringify([...newPaidBookings]));

          console.log('🔍 Cache clearing and re-rendering starting...');

          // Clear cache to ensure UI updates immediately with deleted bookings
          setBookingsCache(new Map());
          console.log('🔍 Cache cleared');

          // Force a re-render by updating a state variable
          setDataLoaded(false);
          console.log('🔍 dataLoaded set to false');

          setTimeout(() => {
            console.log('🔍 Setting dataLoaded to true after 100ms');
            setDataLoaded(true);
          }, 100);

          setMessage('Bokning borttagen');
          console.log('🔍 Message set: Bokning borttagen');

          // Force immediate re-render by updating bookings again with a slight delay
          setTimeout(() => {
            console.log('🔍 Force re-rendering bookings after 50ms');
            setBookings([...updatedBookings]);
          }, 50);

          // Force another re-render after a longer delay to ensure it sticks
          setTimeout(() => {
            console.log('🔍 Force re-rendering bookings after 200ms');
            setBookings([...updatedBookings]);
          }, 200);

          // Force a complete component re-render by updating a key state
          setTimeout(() => {
            console.log('🔍 Force complete re-render after 300ms');
            setDataLoaded(false);
            setTimeout(() => {
              setDataLoaded(true);
              setBookings([...updatedBookings]);
            }, 50);
          }, 300);

          // Force component to re-mount by changing a key
          setTimeout(() => {
            console.log('🔍 Force component re-mount after 400ms');
            setDataLoaded(false);
            setTimeout(() => {
              setDataLoaded(true);
              setBookings([...updatedBookings]);
            }, 50);
          }, 400);

          // Force a final re-render with a longer delay
          setTimeout(() => {
            console.log('🔍 Force final re-render after 500ms');
            setDataLoaded(false);
            setTimeout(() => {
              setDataLoaded(true);
              setBookings([...updatedBookings]);
            }, 50);
          }, 500);

          console.log('🔍 All re-rendering steps scheduled');
        } catch (error) {
          console.error('Mock deletion error:', error);
          setMessage('Kunde inte ta bort bokning');
        }
        return;
      }

      try {
        const response = await fetch(`${API}/bookings/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${code}`
          }
        });

        if (response.ok) {
          const updatedBookings = bookings.filter(b => b.id !== id);
          setBookings(updatedBookings);
          setMessage('Bokningen har tagits bort!');
        }
      } catch (error) {
        console.error('Kunde inte ta bort bokningen:', error);
        setMessage('Något gick fel vid borttagning av bokningen');
      }
    }
  };

  // Boka valda datum - triggering deployment
  const handleBook = async (commentForBooking) => {
    console.log('handleBook called with:', { selectedFamily, selectedDates, commentForBooking });
    console.log('useMockData:', useMockData);
    console.log('useFallbackMock:', useFallbackMock);
    console.log('isLocalDevelopment:', isLocalDevelopment);

    if (!selectedFamily) {
      setMessage("Välj familj först!");
      return;
    }
    if (!Array.isArray(selectedDates) || selectedDates.length === 0) {
      setMessage("Välj minst ett datum först!");
      return;
    }

    // Använd getSelectedDatesArray för att hantera både enskilda datum och intervall
    const datesToBook = getSelectedDatesArray();

    // Check minimum booking length
    if (datesToBook.length < 2) {
      setMessage('Du måste boka minst 2 dagar.');
      return;
    }

    // Klient-sidig kontroll: förhindra dubbelbokning
    console.log('Checking conflicts for dates:', datesToBook.map(d => formatDate(d)));
    console.log('Current bookings in state:', bookings);
    
    // Add detailed logging for production debugging
    console.log('=== BOOKING DEBUG INFO ===');
    console.log('Environment:', { isLocalDevelopment, useMockData, useFallbackMock });
    console.log('Selected family:', selectedFamily);
    console.log('Dates to book:', datesToBook.map(d => formatDate(d)));
    console.log('Current bookings state:', bookings);
    
    // Check what bookings exist for each date using direct booking data
    // Allow double bookings on transition days (check-in/check-out overlaps)
    datesToBook.forEach(date => {
      const dateStr = formatDate(date);
      const existingBookings = bookings.filter(booking => booking.date === dateStr);
      console.log(`Date ${dateStr} existing bookings:`, existingBookings);
      
      // Check if there are existing bookings by other families
      const conflictingBookings = existingBookings.filter(booking => booking.family !== selectedFamily);
      if (conflictingBookings.length > 0) {
        console.log(`Other families found on ${dateStr}, but allowing - backend will handle transition day validation:`, conflictingBookings);
      }
    });
    
    // Don't filter out already booked dates - let backend handle duplicates
    const datesToActuallyBook = datesToBook;

    console.log('No conflicts found, proceeding with booking...');

    // Begränsning: Max 3 framtida perioder (sammanhängande span) per familj
    // Räkna hur många framtida perioder familjen skulle ha inklusive den här bokningen
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const existingFamilyBookings = bookings.filter(b => b.family === selectedFamily);
      const candidateBookings = [
        ...existingFamilyBookings,
        ...datesToActuallyBook.map(d => ({ family: selectedFamily, date: formatDate(d) }))
      ];
      const spansWithCandidate = groupBookings(candidateBookings);
      const futureSpanCount = spansWithCandidate.filter(
        s => s.family === selectedFamily && (new Date(s.start + 'T00:00:00') > today)
      ).length;

      if (futureSpanCount > 3) {
        setMessage('Max 3 framtida perioder per familj. Den här bokningen skulle skapa fler än 3 framtida perioder.');
        return;
      }
    } catch (e) {
      console.warn('Kunde inte validera framtida perioder:', e);
    }

    if (isLocalDevelopment) {
      // Mock booking for local development
      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('🚀 Booking dates:', datesToBook.map(d => formatDate(d)));
        console.log('👨‍👩‍👧‍👦 Selected family:', selectedFamily);

        // Add bookings to mock data - only for dates not already booked by same family
        const newBookings = datesToActuallyBook.map(date => ({
          id: Math.random().toString(36).substr(2, 9).toUpperCase(),
          date: formatDate(date),
          family: selectedFamily,
          startDate: formatDate(datesToBook[0]),
          endDate: formatDate(datesToBook[datesToBook.length - 1]),
          comment: commentForBooking && commentForBooking.trim() ? commentForBooking.trim() : undefined
        }));

        console.log('📝 New bookings created:', newBookings);

        // Update bookings state and save to localStorage
        const updatedBookings = [...bookings, ...newBookings];
        setBookings(updatedBookings);
        saveMockBookings(updatedBookings);

        console.log('💾 Saved updated bookings:', updatedBookings);

        // Clear cache to ensure UI updates with new bookings
        setBookingsCache(new Map());

        // Force a re-render by updating a state variable
        setDataLoaded(false);
        setTimeout(() => setDataLoaded(true), 100);

        setMessage(`Bokade ${datesToActuallyBook.length} nya dagar för ${selectedFamily}`);
        setSelectedDates([]);
        setBookingComment('');
        return;
      } catch (error) {
        console.error('Mock booking error:', error);
        setMessage('Bokning misslyckades');
        return;
      }
    }

    const bookingResults = await Promise.all(
      datesToActuallyBook.map(async (date) => {
        const dateStr = formatDate(date);
        console.log(`🚀 Sending booking request for ${selectedFamily} on ${dateStr}`);
        const response = await fetch(`${API}/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: dateStr, family: selectedFamily, comment: commentForBooking })
        });
        const responseData = await response.json();
        console.log(`📥 Backend response for ${dateStr}:`, { status: response.status, ok: response.ok, data: responseData });
        return { date: dateStr, response, ok: response.ok, status: response.status, data: responseData };
      })
    );

    try {
      console.log('Sending booking requests for dates:', datesToActuallyBook.map(d => formatDate(d)));
      const results = bookingResults;
      console.log('Booking results:', results.map(r => ({ status: r.status, ok: r.ok })));
      
      // Debug: Log detailed response information
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        const date = formatDate(datesToActuallyBook[i]);
        console.log(`Date ${date}: Status ${result.status}, OK: ${result.ok}`);
        if (!result.ok) {
          try {
            const errorData = await result.clone().json();
            console.log(`Error for ${date}:`, errorData);
          } catch (e) {
            console.log(`Could not parse error for ${date}`);
          }
        }
      }
      
      // Count successful bookings vs conflicts (409 = conflict, but still counts as "not ok")
      const successfulBookings = results.filter(r => r.ok);
      const conflictBookings = results.filter(r => r.status === 409);
      const otherFailures = results.filter(r => !r.ok && r.status !== 409);
      
      console.log(`Results summary: ${successfulBookings.length} successful, ${conflictBookings.length} conflicts, ${otherFailures.length} other failures`);
      
      if (otherFailures.length > 0) {
        console.error('Non-conflict failures:', otherFailures);
        setMessage(`${otherFailures.length} av ${results.length} bokningar misslyckades (tekniskt fel)`);
        return;
      }
      
      if (conflictBookings.length > 0 && successfulBookings.length > 0) {
        setMessage(`${successfulBookings.length} dagar bokade, ${conflictBookings.length} hade konflikter.`);
      } else if (conflictBookings.length > 0) {
        setMessage('Alla dagar hade konflikter. Inga bokningar skapades.');
      } else {
        setMessage(`Bokning sparad för ${successfulBookings.length} dagar!`);
      }
      
      console.log('Fetching updated bookings...');
      const response = await fetch(`${API}/bookings`);
      const updatedBookings = await response.json();
      console.log('Updated bookings:', updatedBookings);
      
      // Clear the bookings cache when we get updated bookings
      setBookingsCache(new Map());
      
      // Debug: Check specifically for Thomas bookings around the double-booked dates
      const thomasBookings = updatedBookings.filter(b => b.family === 'Thomas');
      console.log('Thomas bookings after update:', thomasBookings);
      
      // Debug: Check for bookings on the specific dates we just booked
      const bookedDates = datesToActuallyBook.map(d => formatDate(d));
      console.log('Dates we just booked:', bookedDates);
      bookedDates.forEach(dateStr => {
        const bookingsOnDate = updatedBookings.filter(b => b.date === dateStr);
        console.log(`Bookings on ${dateStr}:`, bookingsOnDate);
        console.log(`All bookings for debugging:`, updatedBookings.map(b => ({date: b.date, family: b.family, id: b.id})));
        const oct23Bookings = updatedBookings.filter(b => b.date === '2025-10-23');
        console.log(`All Oct 23 bookings:`, oct23Bookings);
        const oct26Bookings = updatedBookings.filter(b => b.date === '2025-10-26');
        console.log(`All Oct 26 bookings:`, oct26Bookings);
        const nov19Bookings = updatedBookings.filter(b => b.date === '2025-11-19');
        console.log(`🔍 All Nov 19 bookings in state:`, nov19Bookings.map(b => ({family: b.family, id: b.id, date: b.date})));
        if (dateStr === '2025-11-19') {
          console.log(`🎯 CRITICAL: Nov 19 should have Kerstin + Niclas, getAllBookingsForDate returns:`, bookingsOnDate.map(b => ({family: b.family, id: b.id, isCheckIn: b.isCheckIn, isCheckOut: b.isCheckOut})));
        }
        if (dateStr === '2025-10-26') {
          console.log(`CRITICAL: Oct 26 should have Rebecca + Kerstin, but shows:`, bookingsOnDate);
        }
        if (dateStr === '2025-11-05') {
          console.log(`CRITICAL: Nov 5 should have Stig + Thomas, but shows:`, bookingsOnDate);
          console.log(`Nov 5 bookings details:`, bookingsOnDate.map(b => ({family: b.family, id: b.id, date: b.date})));
        }
      });
      
      setBookings(updatedBookings);
      setBookingsCache(new Map()); // Clear cache when bookings are updated
      setSelectedDates([]); // Rensa valet
      setBookingComment('');
    } catch (error) {
      console.error('Booking error:', error);
      setMessage("Något gick fel vid bokning: " + error.message);
    }
  };



  // Ta bort alla bokningar inom valt datumspann (oavsett familj)
  const handleDeleteRange = async () => {
    console.log('🔍 handleDeleteRange called');
    console.log('🔍 Current bookings before range deletion:', bookings.length);
    console.log('🔍 Current bookings:', bookings.map(b => ({id: b.id, family: b.family, date: b.date})));

    if (!Array.isArray(selectedDates) || selectedDates.length === 0) {
      setMessage('Välj datum först.');
      return;
    }

    const dates = getSelectedDatesArray();
    if (dates.length === 0) return;
    const sorted = [...dates].sort((a, b) => a - b);
    const start = formatDate(sorted[0]);
    const end = formatDate(sorted[sorted.length - 1]);

    // Check if any bookings in the range are paid
    const bookingsInRange = bookings.filter(b => b.date >= start && b.date <= end);
    const paidBookingsInRange = bookingsInRange.filter(b => paidBookings.has(b.id));

    if (paidBookingsInRange.length > 0) {
      alert(`Du kan inte ta bort ${paidBookingsInRange.length} betald${paidBookingsInRange.length > 1 ? 'a' : ''} bokning${paidBookingsInRange.length > 1 ? 'ar' : ''} i detta spann. Ångra betalningarna först i "Tidigare betalningar".`);
      return;
    }

    const ok = window.confirm(`Ta bort ALLA bokningar mellan ${start} och ${end}? Detta kan inte ångras.`);
    if (!ok) return;

    if (useMockData) {
      try {
        console.log('🔍 Starting mock range deletion for range:', start, 'to', end);

        const result = await mockDeleteBookingsRange(start, end);
        const updated = bookings.filter(b => !(b.date >= start && b.date <= end));

        console.log('🔍 Mock range deletion completed, updating state...');
        console.log('🔍 Updated bookings after range deletion:', updated.length);
        console.log('🔍 Updated bookings:', updated.map(b => ({id: b.id, family: b.family, date: b.date})));

        setBookings(updated);

        // Also update paid bookings if any bookings in range were marked as paid
        const newPaidBookings = new Set(paidBookings);
        bookingsInRange.forEach(booking => {
          newPaidBookings.delete(booking.id);
        });
        setPaidBookings(newPaidBookings);
        localStorage.setItem('paidBookings', JSON.stringify([...newPaidBookings]));

        console.log('🔍 Cache clearing and re-rendering starting...');

        // Clear cache to ensure UI updates immediately with deleted bookings
        setBookingsCache(new Map());
        console.log('🔍 Cache cleared');

        // Force a re-render by updating a state variable
        setDataLoaded(false);
        console.log('🔍 dataLoaded set to false');

        setTimeout(() => {
          console.log('🔍 Setting dataLoaded to true after 100ms');
          setDataLoaded(true);
        }, 100);

        setSelectedDates([]);
        setMessage(`Tog bort ${result.deleted} bokning(ar).`);
        console.log('🔍 Message set: Tog bort', result.deleted, 'bokning(ar)');

        // Force immediate re-render by updating bookings again with a slight delay
        setTimeout(() => {
          console.log('🔍 Force re-rendering bookings after 50ms');
          setBookings([...updated]);
        }, 50);

        // Force another re-render after a longer delay to ensure it sticks
        setTimeout(() => {
          console.log('🔍 Force re-rendering bookings after 200ms');
          setBookings([...updated]);
        }, 200);

        // Force a complete component re-render by updating a key state
        setTimeout(() => {
          console.log('🔍 Force complete re-render after 300ms');
          setDataLoaded(false);
          setTimeout(() => {
            setDataLoaded(true);
            setBookings([...updated]);
          }, 50);
        }, 300);

        // Force component to re-mount by changing a key
        setTimeout(() => {
          console.log('🔍 Force component re-mount after 400ms');
          setDataLoaded(false);
          setTimeout(() => {
            setDataLoaded(true);
            setBookings([...updated]);
          }, 50);
        }, 400);

        // Force a final re-render with a longer delay
        setTimeout(() => {
          console.log('🔍 Force final re-render after 500ms');
          setDataLoaded(false);
          setTimeout(() => {
            setDataLoaded(true);
            setBookings([...updated]);
          }, 50);
        }, 500);

        console.log('🔍 All re-rendering steps scheduled');
      } catch (e) {
        console.error('Mock raderingsfel:', e);
        setMessage('Kunde inte ta bort bokningarna.');
      }
      return;
    }

    try {
      console.log('🔍 Starting real range deletion for range:', start, 'to', end);

      const res = await fetch(`${API}/bookings`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start, end })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setMessage(data?.message || 'Kunde inte ta bort bokningarna.');
        return;
      }

      console.log('🔍 Real range deletion completed, fetching updated bookings...');

      // Ladda om bokningar
      const response = await fetch(`${API}/bookings`);
      const updatedBookings = await response.json();

      console.log('🔍 Updated bookings fetched:', updatedBookings.length);
      console.log('🔍 Updated bookings:', updatedBookings.map(b => ({id: b.id, family: b.family, date: b.date})));

      setBookings(updatedBookings);

      // Clear cache to ensure UI updates immediately with deleted bookings
      setBookingsCache(new Map());

      // Force a re-render by updating a state variable
      setDataLoaded(false);
      setTimeout(() => setDataLoaded(true), 100);

      setSelectedDates([]);
      setMessage(`Tog bort ${data.deleted ?? 0} bokning(ar).`);

      // Force immediate re-render by updating bookings again with a slight delay
      setTimeout(() => {
        setBookings([...updatedBookings]);
      }, 50);

      // Force another re-render after a longer delay to ensure it sticks
      setTimeout(() => {
        setBookings([...updatedBookings]);
      }, 200);

      // Force a complete component re-render by updating a key state
      setTimeout(() => {
        setDataLoaded(false);
        setTimeout(() => {
          setDataLoaded(true);
          setBookings([...updatedBookings]);
        }, 50);
      }, 300);

      // Force component to re-mount by changing a key
      setTimeout(() => {
        setDataLoaded(false);
        setTimeout(() => {
          setDataLoaded(true);
          setBookings([...updatedBookings]);
        }, 50);
      }, 400);

      // Force a final re-render with a longer delay
      setTimeout(() => {
        setDataLoaded(false);
        setTimeout(() => {
          setDataLoaded(true);
          setBookings([...updatedBookings]);
        }, 50);
      }, 500);
    } catch (error) {
      console.error('Fel vid radering:', error);
      setMessage('Något gick fel vid borttagning.');
    }
  };

  return (
    <div className="App">
      {step !== 0 && <NavBar />}
      {isLoading && <LoadingSpinner message={loadingMessage} />}
      
      {step === 0 && (
        <div className="login-container" style={{ width: '240px', margin: '1.5rem auto 0 auto' }}>
          <h1>Sommarstuga Bokning</h1>
          <div className="login-form">
            <input
              type="password"
              placeholder="Ange kod"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
            />
            <button onClick={(e) => handleLogin(e)}>Logga in</button>
            {message && (
              <div className="message" style={{ marginTop: '8px' }}>
                {message}
              </div>
            )}
          </div>
        </div>
      )}
      {step === 1 && route === '#/booking' && (
        <>
          <div className="calendar-section">
            <h3>Välj start och slutdatum</h3>
            <Calendar
              onClickDay={handleDateClick}
              onChange={handleDateSelection}
              value={selectedDates.length === 2 ? selectedDates : (selectedDates.length === 1 ? selectedDates[0] : null)}
              locale="sv-SE"
              selectRange={false}
              showWeekNumbers={true}
              tileDisabled={({ date, view }) => {
                if (view !== 'month') return false;
                // Don't disable any dates - let user select and backend handle conflicts
                return false;
              }}
              tileClassName={({ date, view }) => {
                const classes = [];
                if (view === 'month') {
                  // Subtle weekend/holiday markings
                  const day = date.getDay();
                  if (day === 0 || day === 6) classes.push('weekend');
                  if (isSwedishHoliday(date)) classes.push('holiday');
                  
                  // Handle check-in/out days for bookings
                  const dateBookings = getAllBookingsForDate(date);
                  
                  if (dateBookings.length > 0) {
                    // Check if this is a check-in or check-out day for any booking
                    const isCheckIn = dateBookings.some(booking => booking.isCheckIn);
                    const isCheckOut = dateBookings.some(booking => booking.isCheckOut);
                    const isMiddleDay = dateBookings.some(booking => booking.isMiddleDay);
                    
                    if (isCheckIn && isCheckOut) {
                      // If same day check-in/out, add both classes for black divider line
                      classes.push('check-in-day', 'check-out-day');
                    } else if (isCheckIn) {
                      classes.push('check-in-day');
                    } else if (isCheckOut) {
                      classes.push('check-out-day');
                    } else if (isMiddleDay) {
                      // For days between check-in and check-out
                      classes.push('booked-day');
                    } else {
                      // For single day bookings
                      classes.push('booked-day');
                    }
                  }
                }
                if (isInSelectedRange(date)) classes.push('selected-date');
                return classes.join(' ');
              }}
              tileContent={({ date, view }) => {
                if (view === 'year') {
                  // Show booking count for each month in year view
                  const year = date.getFullYear();
                  const month = date.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  
                  let bookedDaysCount = 0;
                  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
                    const dayBookings = (() => { const dayStr = formatDate(d); return bookings.filter(booking => booking.date === dayStr); })();
                    if (dayBookings.length > 0) {
                      bookedDaysCount++;
                    }
                  }
                  
                  if (bookedDaysCount > 0) {
                    return (
                      <div style={{ 
                        fontSize: '0.7em', 
                        color: '#b8860b', 
                        fontWeight: 'bold',
                        textAlign: 'center',
                        marginTop: '2px'
                      }}>
                        {bookedDaysCount} dagar
                      </div>
                    );
                  }
                } else if (view === 'month') {
                  const allBookings = getAllBookingsForDate(date);
                  
                  // Debug logging for Dec 12 specifically
                  const dateStr = formatDate(date);
                  if (dateStr === '2025-12-12') {
                    console.log(`🔍 Calendar tile for ${dateStr}:`, allBookings);
                    console.log(`🔍 Raw bookings for ${dateStr}:`, bookings.filter(b => b.date === dateStr));
                  }
                  if (allBookings.length > 0) {
                    // Check if this is a check-in or check-out day with multiple families
                    const checkInBookings = allBookings.filter(b => b.isCheckIn);
                    const checkOutBookings = allBookings.filter(b => b.isCheckOut);

                    // If we have multiple check-ins or check-outs on same day, combine names
                    if (checkInBookings.length > 1) {
                      const combinedName = checkInBookings
                        .map(b => b.family)
                        .filter((family, index, arr) => arr.indexOf(family) === index) // unique
                        .join(' / ');

                      // Truncate if too long (estimate based on typical calendar cell width)
                      const displayName = combinedName.length > 20
                        ? combinedName.substring(0, 17) + '...'
                        : combinedName;

                      return (
                        <div className="booking-info">
                          <div className="family-name combined-names" style={{fontSize: '0.6em', lineHeight: '1', fontWeight: 'bold'}}>
                            {displayName}
                          </div>
                          <div className="check-in-badge" style={{fontSize: '0.5em', color: '#0d6efd', marginTop: '1px'}}>
                            Incheckning
                          </div>
                        </div>
                      );
                    } else if (checkOutBookings.length > 1) {
                      const combinedName = checkOutBookings
                        .map(b => b.family)
                        .filter((family, index, arr) => arr.indexOf(family) === index) // unique
                        .join(' / ');

                      // Truncate if too long
                      const displayName = combinedName.length > 20
                        ? combinedName.substring(0, 17) + '...'
                        : combinedName;

                      return (
                        <div className="booking-info">
                          <div className="family-name combined-names" style={{fontSize: '0.6em', lineHeight: '1', fontWeight: 'bold'}}>
                            {displayName}
                          </div>
                          <div className="check-out-badge" style={{fontSize: '0.5em', color: '#dc3545', marginTop: '1px'}}>
                            Utcheckning
                          </div>
                        </div>
                      );
                    } else {
                      // Single family or middle day - show families with positioning for multiple
                      const uniqueFamilies = [...new Set(allBookings.map(b => b.family))];
                      
                      if (uniqueFamilies.length > 1) {
                        // Multiple families - position them on left and right sides
                        const checkInFamilies = allBookings.filter(b => b.isCheckIn).map(b => b.family);
                        const checkOutFamilies = allBookings.filter(b => b.isCheckOut).map(b => b.family);
                        const middleFamilies = allBookings.filter(b => b.isMiddleDay).map(b => b.family);
                        
                        // Determine which family goes where based on booking type
                        let leftFamily = '';
                        let rightFamily = '';
                        
                        if (checkOutFamilies.length > 0 && checkInFamilies.length > 0) {
                          // Check-out family on left, check-in family on right
                          leftFamily = checkOutFamilies[0];
                          rightFamily = checkInFamilies[0];
                        } else if (uniqueFamilies.length === 2) {
                          // For other cases, just split alphabetically
                          const sortedFamilies = uniqueFamilies.sort();
                          leftFamily = sortedFamilies[0];
                          rightFamily = sortedFamilies[1];
                        }
                        
                        return (
                          <div className="booking-info" style={{display: 'flex', width: '100%', position: 'absolute', bottom: '2px', left: '0'}}>
                            <div className="family-name left-family" style={{fontSize: '0.55em', lineHeight: '1', fontWeight: 'bold', width: '50%', display: 'flex', justifyContent: 'center', overflow: 'hidden'}}>
                              {leftFamily}
                            </div>
                            <div className="family-name right-family" style={{fontSize: '0.55em', lineHeight: '1', fontWeight: 'bold', width: '50%', display: 'flex', justifyContent: 'center', overflow: 'hidden'}}>
                              {rightFamily}
                            </div>
                          </div>
                        );
                      } else {
                        // Single family
                        return (
                          <div className="booking-info">
                            <div className="family-name" style={{fontSize: '0.6em', lineHeight: '1'}}>
                              {uniqueFamilies[0]}
                            </div>
                          </div>
                        );
                      }
                    }
                  }
                }
                return null;
              }}
              onChangeActiveStartDate={() => { /* behåll standard */ }}
              navigationLabel={({ date, view, label }) => {
                if (view === 'month') {
                  // Count booked days in this month
                  const year = date.getFullYear();
                  const month = date.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  
                  let bookedDaysCount = 0;
                  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
                    const dayBookings = (() => { const dayStr = formatDate(d); return bookings.filter(booking => booking.date === dayStr); })();
                    if (dayBookings.length > 0) {
                      bookedDaysCount++;
                    }
                  }
                  
                  return (
                    <div style={{ textAlign: 'center' }}>
                      <div>{label}</div>
                      <div style={{ 
                        fontSize: '0.8em', 
                        color: '#b8860b', 
                        fontWeight: 'bold',
                        marginTop: '2px'
                      }}>
                        {bookedDaysCount} bokade dagar
                      </div>
                    </div>
                  );
                } else if (view === 'year') {
                  // Count booked days in this year
                  const year = date.getFullYear();
                  const firstDay = new Date(year, 0, 1);
                  const lastDay = new Date(year, 11, 31);
                  
                  let bookedDaysCount = 0;
                  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
                    const dayBookings = (() => { const dayStr = formatDate(d); return bookings.filter(booking => booking.date === dayStr); })();
                    if (dayBookings.length > 0) {
                      bookedDaysCount++;
                    }
                  }
                  
                  return (
                    <div style={{ textAlign: 'center' }}>
                      <div>{label}</div>
                      <div style={{ 
                        fontSize: '0.8em', 
                        color: '#b8860b', 
                        fontWeight: 'bold',
                        marginTop: '2px'
                      }}>
                        {bookedDaysCount} bokade dagar
                      </div>
                    </div>
                  );
                } else if (view === 'decade') {
                  // Count booked days in this decade
                  const startYear = Math.floor(date.getFullYear() / 10) * 10;
                  const endYear = startYear + 9;
                  const firstDay = new Date(startYear, 0, 1);
                  const lastDay = new Date(endYear, 11, 31);
                  
                  let bookedDaysCount = 0;
                  for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
                    const dayBookings = (() => { const dayStr = formatDate(d); return bookings.filter(booking => booking.date === dayStr); })();
                    if (dayBookings.length > 0) {
                      bookedDaysCount++;
                    }
                  }
                  
                  return (
                    <div style={{ textAlign: 'center' }}>
                      <div>{label}</div>
                      <div style={{ 
                        fontSize: '0.8em', 
                        color: '#b8860b', 
                        fontWeight: 'bold',
                        marginTop: '2px'
                      }}>
                        {bookedDaysCount} bokade dagar
                      </div>
                    </div>
                  );
                }
                return label;
              }}
            />
            {message && (
              <div className="message" style={{ marginTop: '8px' }}>
                {message}
              </div>
            )}
            <div className="family-select">
              <label htmlFor="family">Välj familj:</label>
              <select
                id="family"
                value={selectedFamily}
                onChange={e => setSelectedFamily(e.target.value)}
              >
                <option value="">-- Välj familj --</option>
                {families.map((family, index) => (
                  <option key={index} value={family}>{family}</option>
                ))}
              </select>
            </div>
            <div className="calendar-actions">
              <button 
                className="show-btn" 
                onClick={() => { adjustSelectionForShow(); setShowRangeModal(true); }}
                disabled={!(Array.isArray(selectedDates) && selectedDates.length >= 1)}
              >
                Visa
              </button>
              {(() => {
                const hasSelection = Array.isArray(selectedDates) && selectedDates.length >= 1;
                console.log('Button render - hasSelection:', hasSelection);
                console.log('Button render - selectedDates:', selectedDates);
                const selectedDateArray = getSelectedDatesArray();
                console.log('Button render - selectedDateArray length:', selectedDateArray.length);

                const conflictsArray = selectedDateArray.map(d => {
                  const dateStr = formatDate(d);
                  const allBookings = getAllBookingsForDate(d);
                  const hasBookings = allBookings.length > 0;
                  console.log(`Button render - Date ${dateStr}: bookings =`, allBookings);
                  console.log(`Button render - Date ${dateStr}: hasBookings =`, hasBookings);
                  console.log(`Button render - Date ${dateStr}: selectedFamily =`, selectedFamily);

                  // 🔍 Analyze booking details for debugging
                  if (hasBookings) {
                    allBookings.forEach((booking, index) => {
                      console.log(`  Booking ${index}: family="${booking.family}", isCheckIn=${booking.isCheckIn}, isCheckOut=${booking.isCheckOut}`);
                    });
                  }

                  // Use the proper conflict detection that allows double booking on transition days
                  const conflict = isDateBookedByOther(d);
                  console.log(`Button render - Date ${dateStr}: conflict = ${conflict}`);
                  return conflict;
                });
                const hasConflicts = conflictsArray.some(conflict => conflict);
                console.log('Button render - hasConflicts:', hasConflicts);

                const tooShort = hasSelection && selectedDateArray.length < 2;
                return (
                  <button className="book-btn" onClick={() => handleBook()} disabled={!hasSelection || hasConflicts || tooShort}>
                    {hasConflicts ? 'Krockar med bokning' : tooShort ? 'Minst 2 dagar krävs' : 'Boka'}
                  </button>
                );
              })()}
              {selectedDates.length > 0 && (
                <button className="clear-btn" onClick={() => setSelectedDates([])}>
                  Rensa val
                </button>
              )}
              {(() => {
                const hasSelection = Array.isArray(selectedDates) && selectedDates.length >= 1;
                const canDelete = hasSelection; // no family required
                const canDeleteAll = bookings.length > 0 && isLocalDevelopment; // only show in local development
                return (
                  <>
                    {canDelete && (
                      <button className="delete-btn" onClick={handleDeleteRange} disabled={!canDelete}>
                        Ta bort markerat spann
                      </button>
                    )}
                    {canDeleteAll && (
                      <button
                        className="clear-all-btn"
                        onClick={() => {
                          if (window.confirm('Ta bort ALLA bokningar och betalda belopp? Detta kan inte ångras.')) {
                            clearAllData();
                            setSelectedDates([]);
                            setMessage('Alla bokningar och betalningar har tagits bort.');
                          }
                        }}
                        style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                      >
                        🗑️ Ta bort ALLA
                      </button>
                    )}
                  </>
                );
              })()}
            </div>

          </div>
          
          {/* Modal för att visa bokningsinformation */}
          {showRangeModal && (
            <div className="booking-modal" onClick={() => setShowRangeModal(false)}>
              <div className="booking-modal-content" onClick={e => e.stopPropagation()}>
                <h3>Information om valda datum</h3>
                {getSelectedDatesArray().length === 0 ? (
                  <p>Inga datum valda.</p>
                ) : (
                  <ul className="range-info-list">
                    {getSelectedDatesArray().map((d, idx) => {
                      const allBookings = (() => { const dayStr = formatDate(d); return bookings.filter(booking => booking.date === dayStr); })();
                      const label = d.toLocaleDateString('sv-SE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      
                      // Group bookings by family to avoid duplicates
                      const bookingsByFamily = {};
                      allBookings.forEach(booking => {
                        if (!bookingsByFamily[booking.family]) {
                          bookingsByFamily[booking.family] = booking;
                        }
                      });
                      const uniqueBookings = Object.values(bookingsByFamily);
                      
                      return (
                        <li key={idx} className={uniqueBookings.length > 0 ? 'range-info booked' : 'range-info free'}>
                          <span className="date-label">{label}</span>
                          {uniqueBookings.length > 0 ? (
                            <div className="booking-details">
                              {uniqueBookings.map((booking, bookingIdx) => (
                                <div key={booking.id} className="single-booking">
                                  <span className="family-info">Bokad av: {booking.family}</span>
                                  {booking.comment && <span className="comment-info">Kommentar: {booking.comment}</span>}
                                  <div className="payment-status">
                                    {(() => {
                                      // Check if this date is the last day of any booking span for this family
                                      const bookingDate = new Date(booking.date);
                                      bookingDate.setHours(0, 0, 0, 0);
                                      
                                      const familyBookings = bookings.filter(b => b.family === booking.family);
                                      const familySpans = groupBookings(familyBookings);
                                      
                                      let isLastDayOfSpan = false;
                                      for (const span of familySpans) {
                                        const spanEndDate = new Date(span.end);
                                        spanEndDate.setHours(0, 0, 0, 0);
                                        if (bookingDate.getTime() === spanEndDate.getTime()) {
                                          isLastDayOfSpan = true;
                                          break;
                                        }
                                      }
                                      
                                      // Check if this date is also the start of another booking (double booking scenario)
                                      const allBookingsThisDate = (() => { const dayStr = formatDate(d); return bookings.filter(booking => booking.date === dayStr); })();
                                      const isStartOfAnotherBooking = allBookingsThisDate.some(otherBooking => {
                                        if (otherBooking.family === booking.family) return false;
                                        
                                        const otherFamilyBookings = bookings.filter(b => b.family === otherBooking.family);
                                        const otherFamilySpans = groupBookings(otherFamilyBookings);
                                        
                                        return otherFamilySpans.some(span => {
                                          const spanStartDate = new Date(span.start);
                                          spanStartDate.setHours(0, 0, 0, 0);
                                          return bookingDate.getTime() === spanStartDate.getTime();
                                        });
                                      });
                                      
                                      // If it's the last day of this family's span, it's always a checkout day
                                      // regardless of whether someone else is checking in
                                      if (isLastDayOfSpan) {
                                      // Check if electricity for this checkout booking is paid
                                      const familyBookings = bookings.filter(b => b.family === booking.family);
                                      const familySpans = groupBookings(familyBookings);
                                      const bookingDate = new Date(booking.date);
                                      bookingDate.setHours(0, 0, 0, 0);

                                      // Check if this is a checkout day with electricity usage paid
                                      let isElectricityPaid = false;
                                      for (const span of familySpans) {
                                        const spanEndDate = new Date(span.end);
                                        spanEndDate.setHours(0, 0, 0, 0);
                                        if (bookingDate.getTime() === spanEndDate.getTime()) {
                                          const kwh = parseFloat(electricityUsage[booking.id]) || 0;
                                          if (kwh > 0 && paidBookings.has(booking.id)) {
                                            isElectricityPaid = true;
                                            break;
                                          }
                                        }
                                      }

                                      return (
                                        <div className="checkout-section">
                                          <span className="checkout-day">Utcheckningsdag</span>
                                          <div className="electricity-input">
                                            <div className="electricity-status">
                                              <label>kWh förbrukning:</label>
                                              <div className="electricity-status-indicator">
                                                {(() => {
                                                  const kwh = parseFloat(electricityUsage[booking.id]);
                                                  const hasUsage = !isNaN(kwh) && kwh > 0;

                                                  if (!hasUsage) {
                                                    return <span className="electricity-unpaid">⚫ Ej rapporterat</span>;
                                                  } else if (isElectricityPaid) {
                                                    return <span className="electricity-paid">🟢 Elkostnad betald ({formatKr(kwh * COST_PER_KWH)} kr)</span>;
                                                  } else {
                                                    return <span className="electricity-unpaid">🟡 Elkostnad ej betald ({formatKr(kwh * COST_PER_KWH)} kr)</span>;
                                                  }
                                                })()}
                                              </div>
                                            </div>
                                            {!isElectricityPaid && (
                                              <div className="kwh-input-group">
                                                <input
                                                  type="number"
                                                  min="0"
                                                  step="0.1"
                                                  value={electricityUsage[booking.id] || ''}
                                                  onChange={(e) => setElectricityUsage(prev => ({
                                                    ...prev,
                                                    [booking.id]: e.target.value
                                                  }))}
                                                  placeholder="0"
                                                  disabled={isElectricityPaid}
                                                />
                                                <span className="kwh-info">
                                                  {electricityUsage[booking.id] ?
                                                    `${formatKr(parseFloat(electricityUsage[booking.id]) * COST_PER_KWH)} kr` :
                                                    '(1,5 kr/kWh)'
                                                  }
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                      }
                                      
                                      // Otherwise show normal payment status
                                      return paidBookings.has(booking.id) ? (
                                        <span className="paid-indicator">✓ Betalt</span>
                                      ) : (
                                        <span className="unpaid-indicator">Ej betalt</span>
                                      );
                                    })()}
                                  </div>
                                  <div className="booking-actions">
                                    <button
                                      className="delete-button"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent event bubbling
                                        console.log('🗑️ Delete button clicked for booking:', booking.id);
                                        handleDeleteBooking(booking.id);
                                      }}
                                    >Ta bort</button>
                                  </div>
                                  {bookingIdx < allBookings.length - 1 && <hr className="booking-separator" />}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="status-info">Ledig</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {/* Snabb-boka direkt från info-rutan */}
                {(() => {
                  const selectedDatesCount = getSelectedDatesArray().length;
                  const estimatedAmount = Math.round(selectedDatesCount * CONFIG.NIGHT_COST);
                  const swishUrl = `swish://pay?phone=${encodeURIComponent(String(CONFIG.SWISH_PHONE))}&amount=${encodeURIComponent(String(estimatedAmount))}&message=${encodeURIComponent(String(CONFIG.SWISH_MESSAGE))}`;
                  const hasRange = Array.isArray(selectedDates) && selectedDates.length === 2;
                  const tooShort = hasRange && selectedDatesCount < 2;
                  // Allow double bookings - let backend handle validation
                  const hasConflicts = false;
                  const noFamily = !selectedFamily;
                  return (
                    <div className="modal-buttons" style={{justifyContent:'space-between'}}>
                      <div style={{display:'flex', flexDirection:'column', gap:'8px', flex:1, marginRight:'12px'}}>
                        <label style={{fontSize:'0.9em', color:'#2c3e50'}}>Familj:</label>
                        <select
                          value={selectedFamily || ''}
                          onChange={(e) => setSelectedFamily(e.target.value || null)}
                          style={{width:'100%', padding:'8px', border:'1px solid #ddd', borderRadius:'4px'}}
                        >
                          <option value="">Välj familj…</option>
                          {families.map((family, index) => (
                            <option key={index} value={family}>{family}</option>
                          ))}
                        </select>
                        {noFamily && (
                          <div style={{color:'#e74c3c', fontSize:'0.9em'}}>
                            Ingen familj vald. Välj en familj för att kunna boka.
                          </div>
                        )}
                        <label style={{fontSize:'0.9em', color:'#2c3e50'}}>Kommentar (valfritt):</label>
                        <textarea 
                          value={bookingComment}
                          onChange={(e) => setBookingComment(e.target.value)}
                          rows={2}
                          placeholder="Lägg till kommentar för dessa datum"
                          style={{width:'100%', padding:'8px', border:'1px solid #ddd', borderRadius:'4px'}}
                        />
                      </div>
                      <div style={{display:'flex', gap:'8px'}}>
                        <button 
                          className="save-button" 
                          onClick={async () => { await handleBook(bookingComment); setShowRangeModal(false); }}
                          disabled={!hasRange || hasConflicts || noFamily || tooShort}
                        >
                          {hasConflicts ? 'Krockar med bokning' : tooShort ? 'Minst 2 dagar krävs' : 'Boka valda datum'}
                        </button>
                        <button className="close-button" onClick={() => setShowRangeModal(false)}>Stäng</button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Modal för att redigera bokning */}
          {editingBooking && (
            <div className="booking-modal" onClick={() => setEditingBooking(null)}>
              <form className="booking-modal-content" onClick={e => e.stopPropagation()} onSubmit={handleUpdateBooking}>
                <h3>Redigera bokning</h3>
                
                {/* Visa andra bokningar för samma datum */}
                <div className="same-date-bookings">
                  <h4>Andra bokningar samma datum:</h4>
                  {getOtherBookingsForDate(editingBooking).length > 0 ? (
                    <ul>
                      {getOtherBookingsForDate(editingBooking).map((booking, index) => (
                        <li key={index}>
                          <strong>{booking.family}</strong>
                          {booking.comment && ` (${booking.comment})`}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Inga andra bokningar detta datum.</p>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Datum:</label>
                  <div className="calendar-container">
                    <Calendar
                      onChange={handleDateChange}
                      value={editingBooking.date instanceof Date ? editingBooking.date : new Date(editingBooking.date)}
                      locale="sv-SE"
                      minDate={new Date()}
                    />
                  </div>
                  <p className="selected-date-display">
                    Valt datum: {editingBooking.date instanceof Date ? 
                      editingBooking.date.toLocaleDateString('sv-SE', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : 
                      new Date(editingBooking.date).toLocaleDateString('sv-SE', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    }
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="family">Familj:</label>
                  <select
                    id="family"
                    value={editingBooking.family}
                    onChange={(e) => setEditingBooking({...editingBooking, family: e.target.value})}
                    required
                  >
                    {families.map((family, index) => (
                      <option key={index} value={family}>{family}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="comment">Kommentar:</label>
                  <textarea
                    id="comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows="3"
                    placeholder="Lägg till en kommentar (valfritt)"
                  />
                </div>

                <div className="modal-buttons">
                  <button type="submit" className="save-button">Spara ändringar</button>
                  <button type="button" className="close-button" onClick={() => setEditingBooking(null)}>Avbryt</button>
                </div>
              </form>
            </div>
          )}
          {/* Meddelandet flyttat ovanför, direkt under kalendern */}
          
        </>
      )}

      {step === 1 && route === '#/billing' && (
        <BillingPage 
          COST_PER_NIGHT={COST_PER_NIGHT}
          getBillingSummary={getBillingSummary}
          currentYear={currentYear}
          getYearlyRevenue={getYearlyRevenue}
          hiddenFamilies={hiddenFamilies}
          hideCountdowns={hideCountdowns}
          onShowPaymentHistory={() => setShowPaymentHistory(true)}
          onOpenBill={(bill) => {
            setSelectedFamilyBill(bill);
            setShowFamilyBillModal(true);
          }}
          onPayAll={(bill) => {
            setSelectedBillForPayment(bill);
            setShowPaymentModal(true);
          }}
        />
      )}

      

      {step === 1 && route === '#/logbook' && (
        <div className="page">
          <Logbook />
        </div>
      )}
      {/* Payment Confirmation Modal */}
      {showPaymentModal && (
        <React.Suspense fallback={<LoadingSpinner message="Laddar..." />}> 
        <PaymentModal 
          selectedBill={selectedBillForPayment}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedBillForPayment(null);
          }}
          onSwishPayment={() => {
            // Säkerställ att beloppet är uppdaterat (t.ex. efter kWh-OK) innan vi öppnar Swish
            const updatedBills = getBillingSummary();
            const updated = updatedBills[selectedBillForPayment.family];
            if (updated) setSelectedBillForPayment(updated);
            // Öppna QR-modal
            setShowPaymentModal(false);
            setShowSwishQR(true);
          }}
          onMarkAsPaid={() => {
            payAllForFamily(selectedBillForPayment.bookings, selectedBillForPayment.family, 'manual_payment');
            setShowPaymentModal(false);
            setSelectedBillForPayment(null);
          }}
          onConfirmElectricity={(kwh) => {
            // Compute override map immediately for live update in modal
            const checkout = getLatestCheckoutBookingForFamily(selectedBillForPayment.family);
            if (checkout) {
              const override = { ...electricityUsage, [checkout.id]: kwh };
              const updatedBills = getBillingSummary(override);
              const updated = updatedBills[selectedBillForPayment.family];
              if (updated) setSelectedBillForPayment(updated);
              // Persist to state/storage in background
              setElectricityUsage(prev => {
                const next = { ...prev, [checkout.id]: kwh };
                localStorage.setItem('electricityUsage', JSON.stringify(next));
                return next;
              });
            }
          }}
          initialKwh={(function() {
            const checkout = getLatestCheckoutBookingForFamily(selectedBillForPayment.family);
            if (!checkout) return '';
            const val = electricityUsage[checkout.id];
            return (val !== undefined && val !== null) ? val : '';
          })()}
        />
        </React.Suspense>
      )}

      {/* Swish QR Code Modal */}
      {showSwishQR && (
        <React.Suspense fallback={<LoadingSpinner message="Laddar..." />}> 
        <SwishQRModal 
          selectedBill={selectedBillForPayment}
          onClose={() => {
            setShowSwishQR(false);
            setSelectedBillForPayment(null);
          }}
          onConfirmPayment={() => {
            payAllForFamily(selectedBillForPayment.bookings, selectedBillForPayment.family, 'swish');
            setShowSwishQR(false);
            setSelectedBillForPayment(null);
          }}
        />
        </React.Suspense>
      )}

      {/* Payment History Modal */}
      {showPaymentHistory && (
        <div className="billing-modal" onClick={() => setShowPaymentHistory(false)}>
          <div className="billing-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Tidigare betalningar</h3>
            
            {paymentHistory.length === 0 ? (
              <p>Inga betalningar registrerade än.</p>
            ) : (
              <div className="payment-history-list">
                {(() => {
                  // Group payments by family
                  const paymentsByFamily = paymentHistory.reduce((acc, payment, originalIndex) => {
                    if (!acc[payment.family]) {
                      acc[payment.family] = [];
                    }
                    acc[payment.family].push({ ...payment, originalIndex });
                    return acc;
                  }, {});

                  // Sort families by total amount (descending)
                  const sortedFamilies = Object.entries(paymentsByFamily)
                    .sort(([, paymentsA], [, paymentsB]) => {
                      const totalA = paymentsA.reduce((sum, p) => sum + p.amount, 0);
                      const totalB = paymentsB.reduce((sum, p) => sum + p.amount, 0);
                      return totalB - totalA;
                    });

                  return sortedFamilies.map(([familyName, familyPayments]) => {
                    const totalAmount = familyPayments.reduce((sum, p) => sum + p.amount, 0);
                    const paymentCount = familyPayments.length;
                    const isExpanded = expandedFamily === familyName;

                    return (
                      <div key={familyName} className="family-payment-group">
                        <div 
                          className="family-header"
                          onClick={() => setExpandedFamily(isExpanded ? null : familyName)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="family-name">{familyName}</div>
                          <div className="family-summary">
                            {paymentCount} betalning{paymentCount !== 1 ? 'ar' : ''} • {formatKr(totalAmount)} kr
                          </div>
                          <div className="expand-indicator">
                            {isExpanded ? '▼' : '▶'}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="family-payments">
                            {(() => {
                              // Group payments by booking spans (consecutive dates)
                              const paymentsBySpan = {};
                              const sortedPayments = familyPayments.sort((a, b) => new Date(a.date) - new Date(b.date));
                              
                              sortedPayments.forEach(payment => {
                                const paymentDate = new Date(payment.date);
                                let spanKey = null;
                                
                                // Find existing span this payment belongs to
                                for (const [key, span] of Object.entries(paymentsBySpan)) {
                                  const spanStart = new Date(span.startDate);
                                  const spanEnd = new Date(span.endDate);
                                  
                                  // Check if payment date is within or adjacent to existing span
                                  const dayBefore = new Date(spanStart);
                                  dayBefore.setDate(dayBefore.getDate() - 1);
                                  const dayAfter = new Date(spanEnd);
                                  dayAfter.setDate(dayAfter.getDate() + 1);
                                  
                                  if (paymentDate >= dayBefore && paymentDate <= dayAfter) {
                                    spanKey = key;
                                    break;
                                  }
                                }
                                
                                if (spanKey) {
                                  // Add to existing span
                                  paymentsBySpan[spanKey].payments.push(payment);
                                  // Update span dates
                                  const currentStart = new Date(paymentsBySpan[spanKey].startDate);
                                  const currentEnd = new Date(paymentsBySpan[spanKey].endDate);
                                  if (paymentDate < currentStart) paymentsBySpan[spanKey].startDate = payment.date;
                                  if (paymentDate > currentEnd) paymentsBySpan[spanKey].endDate = payment.date;
                                } else {
                                  // Create new span
                                  spanKey = `span_${payment.date}_${Math.random()}`;
                                  paymentsBySpan[spanKey] = {
                                    startDate: payment.date,
                                    endDate: payment.date,
                                    payments: [payment]
                                  };
                                }
                              });
                              
                              return Object.entries(paymentsBySpan)
                                .sort(([, spanA], [, spanB]) => new Date(spanB.startDate) - new Date(spanA.startDate))
                                .map(([_, span]) => {
                                  const spanTotal = span.payments.reduce((sum, p) => sum + p.amount, 0);
                                  const nightPayments = span.payments.filter(p => p.type === 'night');
                                  const elPayments = span.payments.filter(p => p.type === 'electricity');
                                  const stableSpanKey = `${familyName}|${span.startDate}|${span.endDate}`;
                                  const isSpanExpanded = expandedPayment === stableSpanKey;
                                  
                                  // Create date range string
                                  const startDate = span.startDate;
                                  const endDate = span.endDate;
                                  const dateRange = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
                                  
                                  return (
                                    <div key={stableSpanKey} className="payment-span-group">
                                      <div 
                                        className="payment-span-header"
                                        onClick={() => setExpandedPayment(isSpanExpanded ? null : stableSpanKey)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <div className="span-date-range">{dateRange}</div>
                                        <div className="span-summary">
                                          {nightPayments.length > 0 && `${nightPayments.length} natt${nightPayments.length !== 1 ? 'er' : ''}`}
                                          {nightPayments.length > 0 && elPayments.length > 0 && ' + '}
                                          {elPayments.length > 0 && `${elPayments.reduce((sum, p) => sum + (p.kwh || 0), 0)} kWh`}
                                          {' • '}{formatKr(spanTotal)} kr
                                        </div>
                                        <div className="expand-indicator-small">
                                          {isSpanExpanded ? '▼' : '▶'}
                                        </div>
                                      </div>
                                      
                                      {isSpanExpanded && (
                                        <div className="span-payment-details">
                                          {span.payments
                                            .sort((a, b) => new Date(b.paidAt) - new Date(a.paidAt))
                                            .map((payment) => (
                                              <div key={payment.originalIndex} className="span-payment-item">
                                                <div className="span-payment-info">
                                                  <div className="payment-detail-line">
                                                    {payment.date} • {payment.type === 'night' ? 'Natt' : payment.kwh ? `El (${payment.kwh} kWh)` : 'El'} • {formatKr(payment.amount)} kr
                                                  </div>
                                                  <div className="payment-timestamp-small">
                                                    {new Date(payment.paidAt).toLocaleDateString('sv-SE')}
                                                  </div>
                                                </div>
                                                <div className="span-payment-actions">
                                                  <button 
                                                    className="cancel-payment-btn-tiny"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (window.confirm('Vill du verkligen ångra denna betalning?')) {
                                                        handleUndoPayment(payment.id);
                                                      }
                                                    }}
                                                    title="Återkalla betalning"
                                                  >
                                                    ↶
                                                  </button>
                                                </div>
                                                
                                                {/* Action log for individual payments */}
                                                {payment.actionLog && (
                                                  <div className="span-payment-log">
                                                    {payment.actionLog.map((action, actionIndex) => (
                                                      <div key={actionIndex} className="span-action-item">
                                                        <span className={`span-action-type ${action.action}`}>
                                                          {action.action === 'payment_confirmed' ? '✓' : '↶'}
                                                        </span>
                                                        <span className="span-action-method">
                                                          {action.method === 'swish' ? 'Swish' : 'Manuell'}
                                                        </span>
                                                        <span className="span-action-time">
                                                          {new Date(action.timestamp).toLocaleString('sv-SE')}
                                                        </span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                });
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
                
                <div className="payment-summary">
                  <div className="summary-row">
                    <span>Totalt antal betalningar:</span>
                    <span>{paymentHistory.length}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total summa:</span>
                    <span>{formatKr(paymentHistory.reduce((sum, p) => sum + p.amount, 0))} kr</span>
                  </div>
                </div>
              </div>
            )}

            <button className="close-modal" onClick={() => setShowPaymentHistory(false)}>
              Stäng
            </button>
          </div>
        </div>
      )}

      {/* Family Bill Detail Modal */}
      {showFamilyBillModal && selectedFamilyBill && (
        <div className="billing-modal" onClick={() => setShowFamilyBillModal(false)}>
          <div className="billing-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Faktureringsdetaljer - {selectedFamilyBill.family}</h3>
            
            <div className="bill-summary">
              <div className="summary-item">
                <label>Antal nätter:</label>
                <span>{selectedFamilyBill.nights}</span>
              </div>
              <div className="summary-item">
                <label>Kostnad per natt:</label>
                <span>{formatKr(COST_PER_NIGHT)} kr</span>
              </div>
              <div className="summary-item">
                <label>Nätter totalt:</label>
                <span>{formatKr(selectedFamilyBill.nightsCost)} kr</span>
              </div>
              {selectedFamilyBill.electricityCost > 0 && (
                <div className="summary-item">
                  <label>Elförbrukning:</label>
                  <span>{formatKr(selectedFamilyBill.electricityCost)} kr</span>
                </div>
              )}
              <div className="summary-item total">
                <label>Total kostnad:</label>
                <span>{formatKr(selectedFamilyBill.totalCost)} kr</span>
              </div>
            </div>

            <div className="booking-spans">
              <h4>Bokningsperioder som ingår i beräkningen (endast passerade datum):</h4>
              {selectedFamilyBill.bookingSpans.map((span, index) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                // Normalize spans to Date objects (spans may have string dates)
                const spanStart = span.start instanceof Date ? new Date(span.start) : new Date(span.start + 'T00:00:00');
                const spanEnd = span.end instanceof Date ? new Date(span.end) : new Date(span.end + 'T00:00:00');
                
                // Only count nights up to today
                const cappedEnd = spanEnd < today ? spanEnd : today;
                
                // Skip spans that are entirely in the future
                if (spanStart >= today) {
                  return null;
                }
                
                const nights = Math.ceil((cappedEnd - spanStart) / (1000 * 60 * 60 * 24));
                const actualNights = Math.max(nights, 0);
                
                    if (actualNights === 0) return null;

                    return (
                      <div key={index} className="span-detail">
                        <div className="span-dates">
                          {spanStart.toLocaleDateString('sv-SE')} - {cappedEnd.toLocaleDateString('sv-SE')}
                          {spanEnd > today && <span className="future-note"> (fram till idag)</span>}
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
                <button className="close-modal" onClick={() => setShowFamilyBillModal(false)}>
                  Stäng
                </button>
              </div>
            </div>
          )}
        </div>
      );
  }

export default App;
